import { CommonModule } from '@angular/common';
import { HttpClient, HttpParams } from '@angular/common/http';
import { AfterViewChecked, Component, ElementRef, HostBinding, HostListener, OnDestroy, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import type { EChartsOption } from 'echarts';
import { MatDialog } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DashboardTitleService } from '@shared-lib/shared-services/dashboard-title.service';
import { PublishedDashboardToolbarService, PublishedDashboardAction } from '@shared-lib/shared-services/published-dashboard-toolbar.service';
import { ActivatedRoute, Router } from '@angular/router';
import { KeycloakService } from 'keycloak-angular';
import { Observable, Subscription, firstValueFrom, forkJoin, of } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';

import { DrillDownComponent } from '../../chart-components/drill-down/drill-down.component';
import { UnifiedTableMatrixChartComponent } from '../../chart-components/unified-table-matrix-chart/unified-table-matrix-chart.component';
import type { TableMatrixCellActionEvent, TableMatrixSaveEvent, TableMatrixWorkflowEvent } from '../../chart-components/unified-table-matrix-chart/unified-table-matrix-chart.types';
import { applyDrillDownSaveToWidgetData } from '../../chart-components/unified-table-matrix-chart/drilldown-save.utils';
import { buildDrillDownTreeFromRows, drillTotalsAtBottom, drillTreeToResponse, normalizeDrillTotalsPlacement, type DrillClientConfig } from '../../chart-components/unified-table-matrix-chart/drilldown-client-tree.utils';
import { SelfServiceFormulaService } from '../shared/self-service-formula.service';
import { DvmFileUploadComponent } from '../../chart-components/dvm-file-upload/dvm-file-upload.component';
import { DvmPopupComponent } from '../../chart-components/dvm-popup/dvm-popup.component';
import { HorizontalBarChartComponent } from '../../chart-components/horizontal-bar-chart/horizontal-bar-chart.component';
import { UnifiedBarColumnChartComponent } from '../../chart-components/unified-bar-column-chart/unified-bar-column-chart.component';
import { UnifiedLineAreaChartComponent } from '../../chart-components/unified-line-area-chart/unified-line-area-chart.component';
import { UnifiedScatterBubbleChartComponent } from '../../chart-components/unified-scatter-bubble-chart/unified-scatter-bubble-chart.component';
import { WaterfallChartComponent } from '../../chart-components/waterfall-chart/waterfall-chart.component';
import { MatSliderComponent } from '../../global-components/mat-slider/mat-slider.component';
import { InputBoxComponent } from '../../global-components/input-box/input-box.component';
import { GlobalDropdownComponent } from '../../global-components/global-dropdown/global-dropdown.component';
import { NumberStatsComponent } from '../../global-components/number-stats/number-stats.component';
import { CapitalCasePipe } from '../../pipes/capital-case.pipe';
import { ShareDashboardDialogComponent } from '@shared-lib/self-service-visualization/dashboard-builder/share-dashboard-dialog/share-dashboard-dialog.component';
import { DvmAvailableViewsRailComponent, toAvailableViews, type AvailableView } from './available-views-rail/available-views-rail.component';
import { DashboardDesign, designToCssVars, resolveDashboardCustomization } from '@shared-lib/self-service-visualization/shared/dashboard-customization.utils';
import { SelfServiceDashboardService } from '@shared-lib/self-service-visualization/shared/self-service-dashboard.service';
import { SelfServiceUiService } from '@shared-lib/self-service-visualization/shared/self-service-ui.service';
import { resolveSelfServiceApiUrl, buildSelfServiceApiUrl } from '@shared-lib/self-service-visualization/shared/self-service-api.utils';
import { SelfServiceApiConfigService } from '@shared-lib/self-service-visualization/shared/self-service-api-config.service';
import { BuilderLayoutConfig } from '@shared-lib/self-service-visualization/shared/self-service-config.types';
import { consumeJustPublishedDashboard, readDynamicDashboardCache, writeDynamicDashboardCache } from '@shared-lib/self-service-visualization/shared/self-service-storage.utils';
import { buildDrillDownWidgetData, buildRecomputedWidgetFields, buildWaterfallOption, mergeDrillDownWidgetData, recomputeEntryHasData, resolveRecomputeChartType, type DrillDownEditConfig } from '@shared-lib/self-service-visualization/shared/self-service-recompute.utils';
import { toSerializable } from '@shared-lib/self-service-visualization/shared/self-service-json.utils';
import { SavedSelfServiceDashboard } from '@shared-lib/self-service-visualization/shared/self-service-types';
import { DvmLandingPageComponent } from '@shared-lib/self-service-visualization/dvm-landing-page/dvm-landing-page.component';
import { LandingPageConfig, extractLandingConfig, isLandingPageLayout } from '@shared-lib/self-service-visualization/dvm-landing-page/landing-page-types';
// Same relative-import pattern side-navbar.component.ts already uses to read
// app environment flags from the shared lib (see that file for precedent).
import { environment } from '../../../../../../../../projects/my-app/src/environments/environment';

interface PublishedWidgetRec {
  id: string;
  widget: any;
  status: 'idle' | 'loading' | 'ready' | 'error';
  error?: string;
}

interface PublishedLayoutItem {
  id: string;
  colSpan: number;
  rowSpan: number;
  /** 0-based grid column start (explicit free placement — NOT an auto-flow
   *  order index). */
  x: number;
  /** 0-based grid row start. */
  y: number;
  /** True after the user explicitly resizes this tile in the published editor. */
  customSize?: boolean;
}

interface ResizeInteraction {
  id: string;
  horizontal: 'left' | 'right' | null;
  vertical: 'top' | 'bottom' | null;
  startX: number;
  startY: number;
  startColSpan: number;
  startRowSpan: number;
  /** The tile's grid x/y at drag start — a 'left'/'top' handle grows the tile
   *  by moving this edge, so the START position shifts too (the opposite edge
   *  must stay put), unlike 'right'/'bottom' which only change the span. */
  startGridX: number;
  startGridY: number;
}

@Component({
  selector: 'app-dvm-published-dashboard',
  standalone: true,
  // Do NOT add HttpClientModule: it re-provides HttpClient with an EMPTY
  // interceptor chain, so requests lose the bearer token and 401.
  imports: [CommonModule, FormsModule, MatTooltipModule, CapitalCasePipe, DrillDownComponent, UnifiedTableMatrixChartComponent, DvmFileUploadComponent, DvmPopupComponent, HorizontalBarChartComponent, UnifiedBarColumnChartComponent, UnifiedLineAreaChartComponent, UnifiedScatterBubbleChartComponent, WaterfallChartComponent, MatSliderComponent, InputBoxComponent, GlobalDropdownComponent, NumberStatsComponent, DvmLandingPageComponent, DvmAvailableViewsRailComponent],
  templateUrl: './dvm-published-dashboard.component.html',
  styleUrl: './dvm-published-dashboard.component.scss',
})
export class DvmPublishedDashboardComponent implements OnInit, OnDestroy, AfterViewChecked {
  private readonly EDIT_GRID_ROW_PX = 48;
  private readonly VIEW_GRID_ROW_PX = 24;
  private readonly VIEW_GRID_ROW_MULTIPLIER = 2;
  /** View mode renders on a FINE column grid (12 × this) so a row of KPI
   *  tiles can be split evenly even when the saved 12-col spans can't (four
   *  KPIs beside a 3-col filter rail = 9 cols → uneven 2/2/2/3). Edit mode
   *  keeps the coarse 12-col grid the tiles were laid out on. 20 divides
   *  cleanly for 2–6 tiles across any 9- or 12-column span. */
  private readonly VIEW_GRID_COL_MULTIPLIER = 20;
  /** Memoised per-row KPI equalisation (fine-grid start/span by tile id). */
  private kpiRowFixesKey = '';
  private kpiRowFixes: Record<string, { start: number; span: number }> = {};
  /** View-mode filter-rail grouping of STANDALONE classic dropdown tiles —
   *  see filterRailBackdrops(). Memoised on the layout + widget-type key. */
  private railGroupsKey = '';
  private railGroups: PublishedLayoutItem[][] = [];
  private railRoleById: Record<string, 'first' | 'middle' | 'last'> = {};
  private readonly route = inject(ActivatedRoute);
  private readonly hostEl = inject(ElementRef);
  private readonly router = inject(Router);
  private readonly http = inject(HttpClient);
  private readonly formulaService = inject(SelfServiceFormulaService);
  private readonly keycloak = inject(KeycloakService);
  private readonly dashboardService = inject(SelfServiceDashboardService);
  private readonly apiConfig = inject(SelfServiceApiConfigService);
  private readonly ui = inject(SelfServiceUiService);
  private readonly dialog = inject(MatDialog);
  private readonly dashboardTitleService = inject(DashboardTitleService);
  private readonly publishedToolbar = inject(PublishedDashboardToolbarService);
  private readonly subs = new Subscription();

  slug = '';
  dashboardName = '';
  originalLayoutJson: Record<string, unknown> | null = null;
  widgets: PublishedWidgetRec[] = [];
  layout: PublishedLayoutItem[] = [];
  loading = true;
  loadError = '';
  editMode = false;
  /** Non-null when the published record is a Landing Page template — rendered
   *  by the dedicated landing component instead of the widget grid. */
  landingConfig: LandingPageConfig | null = null;
  isSharedReadonlyView = false;
  /** The user's OTHER published dashboards, shown as preview cards in the
   *  right-hand "Available Views" rail. Loaded once per component life from
   *  the same email-filtered dynamic_filter call the sidenav uses. */
  availableViews: AvailableView[] = [];
  private availableViewsLoaded = false;
  /** Per-dashboard override of the rail flag (design key `rail.enabled`);
   *  undefined = follow the project environment flag. */
  private designRailEnabled: boolean | undefined;
  /** CSS custom properties last set on the host by applyDashboardDesign —
   *  cleared before the next dashboard's design is applied. */
  private appliedDesignVars: string[] = [];
  isOwner = false;
  private resolvedEmailMemo = '';
  private recomputingComponentIds = new Set<string>();
  private renderVersion = 0;
  private recomputeGeneration = 0;
  /** Per-target staleness, used ONLY by onInputWidgetChange (dropdown/input
   *  filter changes) — the global `recomputeGeneration` counter above is
   *  bumped by EVERY widget change on the dashboard, so a still-relevant
   *  response for e.g. the Vertical dropdown's target table was being
   *  dropped whenever the Applications or Region dropdown fired before it
   *  resolved, even though neither of those touched the same target. Keyed
   *  by componentId so only a NEWER request for that SAME target supersedes
   *  an in-flight one. */
  private targetGeneration = new Map<string, number>();
  private tileDragId: string | null = null;
  private resizeInteraction: ResizeInteraction | null = null;
  /** VIEW-ONLY grid corrections for dropdown widgets. The saved rowSpan is
   *  editor state, not always the natural published height: button/tile filters
   *  can need more rows when options wrap, while old/custom dropdowns can keep
   *  a tall saved footprint that leaves a dead gap before the next chart. After
   *  each render we measure the real control height, let the visible span grow
   *  OR shrink (`viewSpanFixes`), and shift lower tiles down/up just enough to
   *  clear the measured control band (`viewShiftFixes`). Never persisted; edit
   *  mode ignores both so resize handles keep showing the true saved footprint. */
  private viewSpanFixes: Record<string, number> = {};
  private viewShiftFixes: Record<string, number> = {};
  private viewFixApplyScheduled = false;
  /** Ctrl+Z / Ctrl+Shift+Z undo/redo for Edit Layout — one snapshot per
   *  discrete action (a drag-drop, a resize gesture), not per mousemove tick. */
  private layoutUndoStack: PublishedLayoutItem[][] = [];
  private layoutRedoStack: PublishedLayoutItem[][] = [];

  // ── Cross-filter (published view) ─────────────────────────────────────────
  /** Widget `type` strings that render CLIENT-SIDE from `staticData` and can
   *  publish/react to cross-filters. Generic: adding a new client-side chart
   *  (e.g. 'pie-composition') is a one-line addition here. Mirrors the builder's
   *  CLIENT_CHART_CROSS_FILTER registry. */
  private static readonly CROSS_FILTER_TYPES = new Set<string>(['bar-column', 'line-area', 'scatter-bubble']);
  /** The active cross-filter published by a chart click (null = none). Listener
   *  widgets re-render from their original rows filtered by column IN values. */
  private activeCrossFilter: { column: string; values: string[]; sourceId: string } | null = null;
  /** Original (unfiltered) staticData rows per widget id, captured on load so a
   *  filter can be reversed. */
  private crossFilterOriginalRows = new Map<string, Array<Record<string, unknown>>>();
  /** Filter carried in via table→dashboard navigation query params; applied to
   *  the named target components once the dashboard has loaded. */
  private incomingNavFilter: { filterKey: string; filterValue: string; targetComponents: string[] } | null = null;

  @HostBinding('class.dvm-published-host') hostClass = true;

  /** Project-mode branch: this is a read-only, dashboard-only experience —
   *  no route back into the builder, no sharing UI, no "DVM Dashboard" kicker. */
  get projectMode(): boolean {
    const flag = (environment as any).PROJECT_MODE;
    return flag === true || flag === 'true';
  }

  get canShowOwnerActions(): boolean {
    return !this.projectMode && this.isOwner && !this.isSharedReadonlyView;
  }

  /** True when the dashboard name is the SAME text the toolbar already shows as
   *  branding (COMPANY_LOGO_TEXT, or CLIENT_LOGO_TEXT when a client logo is
   *  configured) — showing it again beside itself reads as a duplicated
   *  heading, so the toolbar title is skipped in that one case only. */
  private duplicatesBrandingText(name: string): boolean {
    const normalise = (value: unknown) => String(value ?? '').trim().toLowerCase();
    const dashboard = normalise(name);
    if (!dashboard) return true;
    const env = environment as Record<string, unknown>;
    return [env['COMPANY_LOGO_TEXT'], env['CLIENT_LOGO_TEXT']]
      .map(normalise)
      .some((branding) => !!branding && branding === dashboard);
  }

  get viewItems(): Array<{ rec: PublishedWidgetRec; item: PublishedLayoutItem; renderKey: string }> {
    return this.layout
      .map((item) => {
        const rec = this.widgets.find((widget) => widget.id === item.id);
        return rec ? { rec, item, renderKey: item.id + ':' + this.renderVersion } : null;
      })
      .filter((entry): entry is { rec: PublishedWidgetRec; item: PublishedLayoutItem; renderKey: string } => !!entry);
  }

  ngOnInit(): void {

    this.subs.add(
      this.route.paramMap.subscribe((params) => {
        this.slug = params.get('slug') || '';
        const qp = this.route.snapshot.queryParamMap;
        this.isSharedReadonlyView = qp.get('shared') === '1';
        // Incoming table→dashboard navigation filter (see onTableNavigate on the
        // source dashboard). Applied to the named target components after load.
        const filterKey = qp.get('filterKey') || '';
        const filterValue = qp.get('filterValue');
        this.incomingNavFilter = filterKey && filterValue != null
          ? {
              filterKey,
              filterValue,
              targetComponents: (qp.get('filterTargets') || '').split(',').map((s) => s.trim()).filter(Boolean),
            }
          : null;
        this.loadPublishedDashboard();
      }),
    );

    // The settings gear + its actions live in the shared TOP toolbar (not an
    // in-page bar), so listen for the menu items it fires there.
    this.subs.add(
      this.publishedToolbar.action$.subscribe((action) => this.handleToolbarAction(action)),
    );
    this.syncToolbarState();
  }

  ngOnDestroy(): void {
    this.cancelPendingAutoPersist();
    this.subs.unsubscribe();
    if (this.savedRefreshTimer) { clearTimeout(this.savedRefreshTimer); this.savedRefreshTimer = null; }
    // Stop showing this dashboard's name in the shared top toolbar once we
    // navigate away — otherwise it lingers beside the company logo on the
    // next screen. Same for the settings gear/actions.
    this.dashboardTitleService.clearDashboardTitle();
    this.publishedToolbar.clear();
    // Dropdown picks (Vertical/Application etc.) should only survive an actual
    // browser REFRESH of this same dashboard — a hard reload discards the
    // whole page before Angular ever gets to run this hook, so it never fires
    // for one. Navigating to another in-app screen DOES run it (Angular tears
    // this component down when the route changes), so clearing here means
    // returning to this dashboard later starts from its published default,
    // not whatever was last picked.
    this.clearPersistedDropdownState();
  }

  /** Push the current toolbar state up to the shared top-bar settings menu so
   *  it renders the right actions in the right enabled/edit state. Called
   *  whenever editMode / widget count / ownership changes. */
  private syncToolbarState(): void {
    this.publishedToolbar.setState({
      editMode: this.editMode,
      // A landing page has no widget tiles but still has editable content —
      // without this the gear's Edit Layout / Duplicate stay disabled on it.
      // (For a landing page, 'edit' routes to the landing template editor —
      // see handleToolbarAction.)
      hasWidgets: this.widgets.length > 0 || !!this.landingConfig,
      showOwnerActions: this.canShowOwnerActions,
    });
  }

  /** Runs a settings action fired from the top-bar menu. */
  private handleToolbarAction(action: PublishedDashboardAction): void {
    switch (action) {
      // A landing page's Edit Layout is an in-place card-resize mode (its
      // CONTENT is edited via Back to Builder → landing template editor).
      case 'edit': this.landingConfig ? this.enterLandingEditMode() : this.enterEditMode(); break;
      case 'reset': this.resetDashboardSelections(); break;
      case 'cancel': this.cancelEditMode(); break;
      case 'save': void this.saveLayout(); break;
      case 'back': this.backToBuilder(); break;
      case 'share': this.shareThisDashboard(); break;
      case 'duplicate': void this.duplicateDashboard(); break;
    }
  }

  /** Toolbar chip text confirming an incoming navigation filter arrived (proves
   *  the clicked value reached this dashboard). Empty when there's no filter. */
  get incomingNavFilterLabel(): string {
    const nav = this.incomingNavFilter;
    return nav ? `${nav.filterKey} = ${nav.filterValue}` : '';
  }

  /** Clear the incoming filter chip and restore base data (same as Reset). */
  clearNavigationFilter(): void {
    this.incomingNavFilter = null;
    if (this.originalLayoutJson) {
      this.recomputingComponentIds = new Set();
      this.renderLayout(this.originalLayoutJson);
    }
    // Drop the query params from the URL so a refresh doesn't re-apply them.
    this.router.navigate([], { relativeTo: this.route, queryParams: {} });
  }

  backToBuilder(): void {
    if (!this.slug) return;
    // Hand over the rows currently on screen, not the ones this page loaded
    // with - see layoutWithCurrentTableRows.
    const layoutJson = this.originalLayoutJson ? this.layoutWithCurrentTableRows(this.originalLayoutJson) : null;
    if (layoutJson) writeDynamicDashboardCache(this.slug, layoutJson);
    // Landing pages are authored in the landing template editor, not the
    // chart layout builder.
    if (this.landingConfig) {
      this.router.navigate(['/dvm-landing-builder'], { queryParams: { dashboard: this.slug } });
      return;
    }
    this.router.navigate(['/dvm-layout-builder'], {
      queryParams: {
        view: 'layout',
        dashboard: this.slug,
        dashboardId: this.dashboardName || this.slug,
        preview: 'true',
      },
      state: {
        selfServiceView: 'layout',
        dashboardSlug: this.slug,
        dashboardId: this.dashboardName || this.slug,
        layoutJson,
        preview: true,
      },
    });
  }

  shareThisDashboard(): void {
    if (!this.slug || !this.originalLayoutJson) return;
    this.dialog.open(ShareDashboardDialogComponent, {
      width: 'min(560px, 94vw)',
      maxWidth: '94vw',
      autoFocus: false,
      restoreFocus: false,
      panelClass: 'ss-share-dialog-panel',
      data: {
        shareLink: `${window.location.origin}/dvm-published/${this.slug}?shared=1`,
        shareSlug: this.slug,
        layoutJson: this.originalLayoutJson,
        dashboardName: this.dashboardName || this.slug,
      },
    });
  }

  /** "Duplicate to new dashboard": clone every chart on THIS dashboard into a
   *  brand-new, fully independent one under a name the user types — publish
   *  (recompute/edit/reset/share) all work on the copy exactly like any other
   *  dashboard from here on, since it's a genuine new `dashboard_layouts` row,
   *  not a reference back to this one. */
  async duplicateDashboard(): Promise<void> {
    if (!this.originalLayoutJson) return;
    const suggested = `${this.dashboardName || this.slug} copy`;
    const typed = window.prompt('Name for the new dashboard:', suggested);
    if (typed == null) return; // cancelled
    const newName = typed.trim();
    if (!newName) return;

    const cloned = this.deepCloneRecord(this.originalLayoutJson);
    const withFreshIds = this.remapComponentIdsForCopy(cloned);
    withFreshIds['dashboard-name'] = newName;
    withFreshIds['name'] = newName;
    // Force a fresh dashboard_id — never reuse the source's, or publish would
    // update the SOURCE row instead of inserting this as a new one.
    delete withFreshIds['dashboardId'];
    delete withFreshIds['publishedAt'];

    const newSlug = this.buildCopySlug(newName);
    try {
      const email = await this.resolveCurrentEmail();
      await firstValueFrom(this.dashboardService.publishLayoutToApi(withFreshIds, email, newSlug));
      writeDynamicDashboardCache(newSlug, withFreshIds);
      this.ui.showSuccess(`"${newName}" created with a copy of every chart.`);
      window.dispatchEvent(new CustomEvent('dvm:dashboard-published', { detail: { dashboardId: newSlug, name: newName } }));
      this.router.navigate(['/dvm-published', newSlug]);
    } catch (error) {
      console.error('[dvm-published] duplicate dashboard failed:', error);
      this.ui.showError('Could not create the duplicate — please try again.');
    }
  }

  private buildCopySlug(name: string): string {
    const base = String(name || '').trim().toLowerCase().replace(/[^a-z0-9_-]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '') || 'untitled-dashboard';
    return `${base}-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
  }

  /** Mint a fresh id for every componentId/widgetId/id found anywhere in the
   *  cloned layout, then rewrite EVERY reference to it (input/dropdown target
   *  lists, layout.items, group-card children, etc.) via an exact quoted-string
   *  swap on the serialized JSON — simpler and more complete than hand-walking
   *  every place an id could be referenced, and safe because these ids are
   *  distinctively prefixed (CMP-/tile-/GRP-/dvm-tile-…), so an accidental
   *  collision with unrelated user text is effectively impossible. Without
   *  this, the copy's charts would share ids with the source's — editing or
   *  recomputing one could silently clobber the other's saved component config. */
  private remapComponentIdsForCopy(layoutJson: Record<string, unknown>): Record<string, unknown> {
    const ids = new Set<string>();
    const collect = (node: unknown): void => {
      if (Array.isArray(node)) { node.forEach(collect); return; }
      if (!node || typeof node !== 'object') return;
      const record = node as Record<string, unknown>;
      for (const key of ['componentId', 'widgetId', 'id']) {
        const value = record[key];
        if (typeof value === 'string' && value) ids.add(value);
      }
      Object.values(record).forEach(collect);
    };
    collect(layoutJson);

    const suffix = () => Math.random().toString(36).slice(2, 7);
    let json = JSON.stringify(layoutJson);
    ids.forEach((id) => {
      json = json.split(`"${id}"`).join(`"${id}-copy-${suffix()}"`);
    });
    return JSON.parse(json);
  }

  enterEditMode(): void {
    this.editMode = true;
    // Fresh undo/redo history per edit session — nothing from a previous
    // Edit Layout visit should be un-doable after Save/Cancel closed it.
    this.layoutUndoStack = [];
    this.layoutRedoStack = [];
    this.syncToolbarState();
  }

  /** Landing-page Edit Layout: the card grid is resized in place (drag handles
   *  rendered by the landing component); snapshot the config so Cancel can
   *  restore the pre-edit card sizes. */
  private landingConfigSnapshot: string | null = null;
  enterLandingEditMode(): void {
    this.landingConfigSnapshot = JSON.stringify(this.landingConfig);
    this.editMode = true;
    this.syncToolbarState();
  }

  cancelEditMode(): void {
    if (this.landingConfig) {
      if (this.landingConfigSnapshot) {
        try { this.landingConfig = JSON.parse(this.landingConfigSnapshot); } catch { /* keep current */ }
      }
      this.landingConfigSnapshot = null;
      this.editMode = false;
      this.syncToolbarState();
      return;
    }
    this.editMode = false;
    this.tileDragId = null;
    this.resizeInteraction = null;
    this.layout = this.buildLayoutFromJson(this.originalLayoutJson, this.widgets);
    this.layoutUndoStack = [];
    this.layoutRedoStack = [];
    this.syncToolbarState();
  }

  /** Snapshot the layout BEFORE a discrete edit (a drag-drop, or the START of a
   *  resize gesture — not every mousemove tick) so Ctrl+Z has something to
   *  restore. A fresh edit always clears redo — you can't redo past a NEW
   *  change, same as any standard undo stack. */
  private snapshotLayoutForUndo(): void {
    this.layoutUndoStack.push(this.layout.map((item) => ({ ...item })));
    if (this.layoutUndoStack.length > 50) this.layoutUndoStack.shift();
    this.layoutRedoStack = [];
  }

  /** Ctrl+Z while editing the layout — undo the last drag/resize. Ignored
   *  outside Edit Layout and while typing in a text field (so it doesn't
   *  fight a text input's own native undo, e.g. the dashboard name). */
  @HostListener('window:keydown', ['$event'])
  onLayoutUndoRedoKeydown(event: KeyboardEvent): void {
    if (!this.editMode) return;
    if (!(event.ctrlKey || event.metaKey) || event.key.toLowerCase() !== 'z') return;
    const target = event.target as HTMLElement | null;
    const tag = target?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable) return;
    event.preventDefault();
    if (event.shiftKey) this.redoLayoutChange();
    else this.undoLayoutChange();
  }

  undoLayoutChange(): void {
    const previous = this.layoutUndoStack.pop();
    if (!previous) return;
    this.layoutRedoStack.push(this.layout.map((item) => ({ ...item })));
    this.layout = previous;
    this.notifyChartsAboutResize();
  }

  redoLayoutChange(): void {
    const next = this.layoutRedoStack.pop();
    if (!next) return;
    this.layoutUndoStack.push(this.layout.map((item) => ({ ...item })));
    this.layout = next;
    this.notifyChartsAboutResize();
  }

  async saveLayout(): Promise<void> {
    // Landing page: persist the resized card sizes by republishing the SAME
    // layout_json with the current `landing` config swapped in — the widget
    // buildPersistedLayoutJson path below reads pageContent, which a landing
    // page doesn't have.
    if (this.landingConfig && this.originalLayoutJson) {
      try {
        const nextLayoutJson: Record<string, unknown> = {
          ...this.originalLayoutJson,
          landing: JSON.parse(JSON.stringify(this.landingConfig)),
          publishedAt: new Date().toISOString(),
        };
        await this.publishLayoutJson(nextLayoutJson, { broadcast: false });
        this.landingConfigSnapshot = null;
        this.ui.showSuccess('Layout saved.');
      } catch (error) {
        console.error('[dvm-published] could not persist landing layout:', error);
        this.ui.showError('Could not save the layout — please try again.');
      } finally {
        this.editMode = false;
        this.syncToolbarState();
      }
      return;
    }
    // eslint-disable-next-line no-console
    console.log('[dvm-published] saveLayout() called', { slug: this.slug, layout: this.layout });
    try {
      const nextLayoutJson = this.buildPersistedLayoutJson();
      // eslint-disable-next-line no-console
      console.log('[dvm-published] buildPersistedLayoutJson() ->', nextLayoutJson);
      if (!nextLayoutJson) {
        // eslint-disable-next-line no-console
        console.log('[dvm-published] no originalLayoutJson — nothing to publish, aborting');
        return;
      }
      await this.publishLayoutJson(nextLayoutJson, { broadcast: true });
      this.ui.showSuccess('Layout saved.');
    } catch (error) {
      // This used to be a console.error only — the layout edit was silently
      // dropped on any backend failure (expired token, network blip, 500…)
      // while the UI still dropped out of edit mode looking like it saved.
      // Surface it so the user knows to retry instead of assuming it worked.
      console.error('[dvm-published] could not persist edited layout:', error);
      const detail = error instanceof Error ? error.message : '';
      this.ui.showError(detail ? `Could not save the layout: ${detail}` : 'Could not save the layout — please try again.');
    } finally {
      this.editMode = false;
      this.syncToolbarState();
    }
  }

  /** Shared network round-trip behind `saveLayout()` (explicit "Save Layout"
   *  button) and `persistInputOverride()` (auto-persist after a slider/input
   *  recompute) — both need the exact same save+publish call, just triggered
   *  differently and with different user-facing feedback. */
  private async publishLayoutJson(
    nextLayoutJson: Record<string, unknown>,
    // `dvm:dashboard-published` triggers a full sidebar rebuild + API re-query.
    // Right after an explicit Save; a page-freezing waste after auto-persist,
    // which only rewrites this dashboard's own row. Off by default.
    options: { broadcast?: boolean } = {},
  ): Promise<void> {
    // Keep this write bound to its original dashboard across navigation.
    const slug = this.slug;
    const renderVersion = this.renderVersion;
    const dashboardName = this.dashboardName || slug || 'Untitled dashboard';
    const now = new Date().toISOString();
    const savedDashboard: SavedSelfServiceDashboard = {
      id: String(nextLayoutJson['id'] ?? nextLayoutJson['dashboardId'] ?? slug ?? this.createDashboardId()),
      name: dashboardName,
      createdAt: String(nextLayoutJson['createdAt'] ?? now),
      updatedAt: now,
      items: [],
      layout: this.buildSavedDashboardLayout(nextLayoutJson),
      status: 'published',
      publishedAt: String(nextLayoutJson['publishedAt'] ?? now),
      config: nextLayoutJson,
    };
    await firstValueFrom(this.dashboardService.saveDashboard(savedDashboard));
    const email = await this.resolveCurrentEmail();
    // This dashboard is already published (we're viewing/editing it here), so
    // look up its existing row's uuid — present → update that row; absent →
    // this email doesn't own an existing row yet, insert a new one. Matched
    // by `slug` (the exact dashboard_id the dashboard was published
    // under, from the route) — NOT the display name, which may not be unique
    // and doesn't include the id's uniqueness suffix.
    const existingRecord = email
      ? await firstValueFrom(this.dashboardService.findOwnedDashboardLayoutRecord(slug, email))
      : null;
    const publishUrl = await this.resolvePublishApi(nextLayoutJson);
    const publishPayload = this.buildPublishEnvelope(nextLayoutJson, email, slug, existingRecord?.uuid ?? null);
    if (publishUrl) {
      await firstValueFrom(this.http.post(publishUrl, publishPayload));
    } else {
      await firstValueFrom(this.dashboardService.publishLayoutToApi(nextLayoutJson, email, slug));
    }
    if (this.slug === slug && this.renderVersion === renderVersion) {
      this.originalLayoutJson = nextLayoutJson;
    }
    writeDynamicDashboardCache(slug, nextLayoutJson);
    if (options.broadcast) {
      window.dispatchEvent(new CustomEvent('dvm:dashboard-published'));
    }
  }

  /** Debounced auto-persist fired after a slider/input-box change recomputes
   *  its target KPIs — without this, `recomputeChartsOnOpen`/reload always
   *  re-pulled the original business-computed values, so a user's override
   *  only ever lived in `this.widgets` for the current tab and silently reset
   *  on refresh. Debounced (not saved on every intermediate value) because a
   *  number input box emits on every keystroke, not just on release. Owner-only
   *  — a shared read-only viewer nudging a slider must not rewrite the
   *  published dashboard everyone else sees. */
  private autoPersistHandle: ReturnType<typeof setTimeout> | null = null;
  private autoPersistGeneration = 0;
  private cancelPendingAutoPersist(): void {
    if (this.autoPersistHandle) clearTimeout(this.autoPersistHandle);
    this.autoPersistHandle = null;
    this.autoPersistGeneration += 1;
  }
  private scheduleAutoPersistOverride(): void {
    if (!this.isOwner || this.isSharedReadonlyView) return;
    if (this.autoPersistHandle) clearTimeout(this.autoPersistHandle);
    this.autoPersistHandle = setTimeout(() => {
      this.autoPersistHandle = null;
      void this.persistInputOverride();
    }, 600);
  }

  private async persistInputOverride(): Promise<void> {
    const generation = ++this.autoPersistGeneration;
    try {
      const nextLayoutJson = this.buildPersistedLayoutJson();
      if (!nextLayoutJson) return;
      await this.publishLayoutJson(nextLayoutJson);
      // A newer slider/input change (and its own auto-persist) superseded this
      // one while the request was in flight — its result already reflects the
      // latest state, so don't let this stale response undo it.
      if (generation !== this.autoPersistGeneration) return;
    } catch (error) {
      console.warn('[dvm-published] auto-persist of input override failed:', error);
    }
  }
  /** "Reset" used to just replay `this.originalLayoutJson` — but that gets
   *  overwritten with the LAST auto-persisted state on every slider/dropdown
   *  change (see `publishLayoutJson`), so once any input had been touched even
   *  once, Reset silently redrew that same override back at you: no API call,
   *  no actual return to default. Drive every input widget back to ITS OWN
   *  `defaultValue` through the real compute call instead — the exact same
   *  path `onInputWidgetChange` already uses for a live edit — so Reset
   *  visibly recomputes every dependent chart from true default data (and
   *  persists that as the new saved state, same as any other input change). */
  resetDashboardSelections(onComplete?: () => void, persist = true): void {
    if (!this.originalLayoutJson) { onComplete?.(); return; }
    this.editMode = false;
    this.tileDragId = null;
    this.resizeInteraction = null;

    // EVERY selection the user can apply has to go, not just input values.
    // Nulling `incomingNavFilter` alone (all this used to do) hid the toolbar
    // chip and nothing else.
    const hadNavFilter = !!this.incomingNavFilter;
    this.incomingNavFilter = null;
    // A click-driven cross-filter is a selection too — clearing it restores each
    // reacting chart's unfiltered rows (and its listening waterfalls).
    if (this.activeCrossFilter) {
      this.activeCrossFilter = null;
      this.applyPublishedCrossFilter();
    }
    // Dropdown-driven client-side filters (bar-column/line-area/scatter-bubble
    // rows filtered locally) are selections too — restore the full rows.
    if (this.activeDropdownFilters.size) {
      this.activeDropdownFilters.clear();
      this.applyDropdownFiltersToClientWidgets();
    }
    // applyIncomingNavigationFilter WRITES filterKey/filterValue/filters INTO
    // each target chart's own payload, so the filter outlived Reset entirely —
    // and rode along into the auto-persisted override. Strip it, then recompute
    // those charts below so they come back with unfiltered data.
    const unfilteredChartRecs = this.stripSelectionFiltersFromPayloads();
    if (hadNavFilter) {
      // Same URL cleanup clearNavigationFilter() does. Without it a refresh
      // re-applies the very filter Reset just cleared.
      this.router.navigate([], { relativeTo: this.route, queryParams: {} });
    }

    // NOT calling renderLayout() here: it kicks off its OWN async recompute
    // (maybeRecomputeChartsOnOpen) under a freshly-bumped `recomputeGeneration`
    // — then looping onInputWidgetChange() per input right after bumped that
    // SAME shared counter again on every call. By the time either request
    // actually came back (fast — this wasn't a timeout), its stale-generation
    // guard (`generation !== this.recomputeGeneration`) silently discarded the
    // response, so `setWidgetStatus(..., 'ready')` never ran for the charts
    // renderLayout had just marked 'loading' — stuck spinner forever, even
    // though the network tab showed the calls completing fine. Reset each
    // input's own value directly and batch every recompute under ONE shared
    // generation instead (below), so nothing invalidates anything else's
    // response.
    const inputRecs = this.widgets.filter((rec) => this.isInputWidget(rec.widget));

    // Reset EVERY input's own displayed value first — a slider's handle/number
    // box reads `widget.data.data.rangeStart/rangeEnd` and an input-box reads
    // `widget.value`, NEITHER of which lives under `widget.payload` (that's
    // only the compute-call body). Mutating payload alone — what this used to
    // do — never touched what the slider/input actually renders, so the
    // control itself looked unchanged even though its target charts updated.
    // Building a NEW widget object (not mutating in place) and swapping it
    // into `this.widgets` also matters: the child component's `[data]` input
    // only re-syncs (ngOnChanges) on a REFERENCE change, not a deep mutation.
    // The silent on-open reset (`persist === false`, called from renderLayout on
    // every dashboard open) restores the viewer's last dropdown pick on THIS
    // dashboard when one is on record — otherwise it always wins over
    // `defaultValueForInputWidget`'s published default, which is exactly the
    // "Vertical reverts to Mining after a refresh" bug. A record only exists
    // here on an actual browser refresh: `ngOnDestroy` clears it the moment
    // this component is torn down for navigating to another in-app screen, so
    // returning to this dashboard later has nothing to restore. An EXPLICIT
    // toolbar "Reset" click (`persist === true`) also always goes back to the
    // true configured default and drops the record so it can't resurface.
    const persistedDropdownState = persist ? null : this.loadPersistedDropdownState();
    if (persist) this.clearPersistedDropdownState();
    const resetJobs = inputRecs.map((rec) => {
      const widget = rec.widget;
      const type = String(widget?.type ?? '').toLowerCase();
      const persistedValue = type === 'dropdown' && persistedDropdownState
        ? persistedDropdownState[String(widget?.componentId ?? '')]
        : undefined;
      const rawValue = persistedValue !== undefined ? persistedValue : this.defaultValueForInputWidget(widget);
      const value = rawValue && typeof rawValue === 'object' && 'end' in (rawValue as Record<string, unknown>)
        ? (rawValue as Record<string, unknown>)['end']
        : rawValue;
      const payload = { ...(widget?.payload ?? {}), value };
      // Dropdown targets are re-resolved against the CURRENT chart set, but
      // CONSTRAINED to the ones the author picked in the build form. This used
      // to send every chart unconditionally, so picking one target still
      // recomputed (and published a payload listing) all of them.
      if (type === 'dropdown') {
        const configured = this.configuredDropdownTargetIds(payload);
        const rebuilt = this.widgets
          .filter((r) => String(r.widget?.componentId ?? '') !== String(widget?.componentId ?? '')
            // Explicit picker choice wins outright, whatever the target's type
            // (a chart OR another dropdown/input) — falling back to "only
            // charts" is what silently dropped an explicitly configured
            // dropdown/input target. No picker selection falls back to the
            // legacy global-filter default of every chart widget.
            && (configured.size ? configured.has(String(r.widget?.componentId ?? '')) : this.isChartWidget(r.widget)))
          .map((r) => ({ componentId: String(r.widget?.componentId ?? ''), label: r.widget?.title ?? '', field: payload['field'] ?? '' }))
          .filter((target) => !!target.componentId);
        if (rebuilt.length) payload['targets'] = rebuilt;
        else if (!Array.isArray(payload['targets'])) payload['targets'] = [];
      }
      const nextWidget = {
        ...widget,
        payload,
        ...(type === 'slider'
          ? { data: { ...(widget?.data ?? {}), data: { ...(widget?.data?.data ?? {}), rangeStart: value, rangeEnd: value } } }
          : type === 'input-box' ? { value } : {}),
      };
      return { id: rec.id, widget: nextWidget, payload, targetIds: this.extractTargetIds(payload['targets']) };
    });

    const jobById = new Map(resetJobs.map((job) => [job.id, job]));
    this.widgets = this.widgets.map((rec) => (jobById.has(rec.id) ? { ...rec, widget: jobById.get(rec.id)!.widget } : rec));

    const jobs = resetJobs.filter((job) => job.targetIds.length);

    const allTargetIds = new Set<string>();
    jobs.forEach((job) => job.targetIds.forEach((id) => allTargetIds.add(id)));
    // A chart that was carrying a navigation/cross filter but isn't any input's
    // target still needs one plain recompute to come back unfiltered — nothing
    // else would ever re-request it.
    const filterClearRecs = unfilteredChartRecs.filter(
      (rec) => !allTargetIds.has(String(rec.widget?.componentId ?? '').trim()),
    );

    if (!jobs.length && !filterClearRecs.length) {
      this.recomputingComponentIds = new Set();
      onComplete?.();
      return;
    }

    const busyIds = new Set<string>(allTargetIds);
    filterClearRecs.forEach((rec) => {
      const id = String(rec.widget?.componentId ?? '').trim();
      if (id) busyIds.add(id);
    });
    this.recomputingComponentIds = busyIds;
    const generation = ++this.recomputeGeneration;

    // ONE batch under ONE generation (see the note above on stale-generation
    // guards eating responses). The two kinds of request need different apply
    // calls, so each resolves to a thunk that applies its own result and reports
    // whether anything landed — that keeps them in the same forkJoin.
    const inputRequests = jobs.map((job) =>
      this.http.post<Record<string, unknown>>(this.computeUrlForWidget(job.widget), job.payload).pipe(
        map((response) => () => {
          this.applyRecomputeResults(response, job.targetIds);
          return true;
        }),
        catchError((error) => {
          console.warn('[dvm-published] reset recompute failed:', error);
          return of(() => false);
        }),
      ),
    );

    const filterClearRequests = filterClearRecs.map((rec) =>
      this.http.post<Record<string, unknown>>(this.computeUrlForWidget(rec.widget), rec.widget?.payload ?? {}).pipe(
        map((response) => () => {
          const applied = this.applyComputeResultToWidget(rec.id, rec.widget, response);
          this.setWidgetStatus(rec.id, 'ready');
          return applied;
        }),
        catchError((error) => {
          console.warn('[dvm-published] reset filter-clear recompute failed:', error);
          return of(() => {
            this.setWidgetStatus(rec.id, 'ready');
            return false;
          });
        }),
      ),
    );

    this.subs.add(
      forkJoin([...inputRequests, ...filterClearRequests]).pipe(
        finalize(() => { if (generation === this.recomputeGeneration) this.recomputingComponentIds = new Set(); }),
      ).subscribe((appliers) => {
        if (generation !== this.recomputeGeneration) return;
        let applied = false;
        appliers.forEach((apply) => { if (apply()) applied = true; });
        // One persist for the whole reset, not one per input.
        if (applied && persist) this.scheduleAutoPersistOverride();
        onComplete?.();
      }),
    );
  }

  /** Remove navigation-filter / cross-filter keys from every CHART's compute
   *  payload, returning the records that were carrying one (already swapped into
   *  `this.widgets`). These keys are written into the payload itself — see
   *  applyIncomingNavigationFilter and the cross-filter recomputes — so they
   *  persist across every later compute until something takes them out. */
  private stripSelectionFiltersFromPayloads(): PublishedWidgetRec[] {
    const touched: PublishedWidgetRec[] = [];
    this.widgets = this.widgets.map((rec) => {
      if (!this.isChartWidget(rec.widget)) return rec;
      const payload = rec.widget?.payload as Record<string, unknown> | undefined;
      if (!payload) return rec;
      if (!('filterKey' in payload) && !('filterValue' in payload) && !('filters' in payload)) return rec;
      const next = { ...payload };
      delete next['filterKey'];
      delete next['filterValue'];
      delete next['filters'];
      const nextRec = { ...rec, widget: { ...rec.widget, payload: next } };
      touched.push(nextRec);
      return nextRec;
    });
    return touched;
  }

  /** Reconstruct the SAME default-value shape the builder gives each input
   *  type at generate time (see DvmLayoutBuilderComponent.buildDropdownTile /
   *  buildInputTile): sliders/input boxes store a plain default; dropdowns
   *  always bind an ARRAY (GlobalDropdownComponent.writeValue), single-select
   *  wrapping its lone default into a one-item array. */
  private defaultValueForInputWidget(widget: any): unknown {
    const defaultValue = widget?.payload?.defaultValue;
    const type = String(widget?.type ?? '').toLowerCase();
    if (type === 'dropdown') {
      if (Array.isArray(defaultValue)) return defaultValue;
      return defaultValue ? [defaultValue] : [];
    }
    // No default was configured in the builder. A slider's own seed value is its
    // range minimum (buildInputControlPayload uses the same fallback), and
    // pushing null instead recomputed the charts on null while the slider's
    // handle stayed put — MatSlider ignores a null pushed value.
    if (defaultValue === null || defaultValue === undefined) {
      const min = Number((widget?.payload?.range as Record<string, unknown> | undefined)?.['min']);
      return type === 'slider' && Number.isFinite(min) ? min : null;
    }
    return defaultValue;
  }

  onTileDragStart(id: string, event: DragEvent): void {
    if (!this.editMode) return;
    this.tileDragId = id;
    event.dataTransfer?.setData('text/plain', id);
    if (event.dataTransfer) event.dataTransfer.effectAllowed = 'move';
  }

  onTileDragOver(event: DragEvent): void {
    if (!this.editMode || !this.tileDragId) return;
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
  }

  onCanvasDragOver(event: DragEvent): void {
    if (!this.editMode || !this.tileDragId) return;
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
  }

  /** Dropped directly on another tile — swap the two tiles' positions rather
   *  than computing a pixel-precise cell under the cursor (dropping ON a tile
   *  unambiguously means "I want to take that tile's spot"). */
  onTileDrop(targetId: string, event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (!this.editMode) return;
    const sourceId = this.tileDragId || event.dataTransfer?.getData('text/plain') || '';
    if (sourceId && sourceId !== targetId) {
      this.snapshotLayoutForUndo();
      this.swapTilePositions(sourceId, targetId);
    }
    this.tileDragId = null;
  }

  /** Dropped on empty canvas background — free placement: move the tile to
   *  whatever grid cell is under the cursor. If that cell is actually occupied
   *  by another tile (the empty-looking area was really inside its footprint,
   *  e.g. a gap next to a short tile under a tall one), swap with it instead
   *  of silently overlapping two tiles on top of each other. */
  onCanvasDrop(event: DragEvent): void {
    event.preventDefault();
    if (!this.editMode) return;
    const sourceId = this.tileDragId || event.dataTransfer?.getData('text/plain') || '';
    const source = this.layout.find((item) => item.id === sourceId);
    const canvas = event.currentTarget as HTMLElement | null;
    if (!source || !canvas) { this.tileDragId = null; return; }

    const cell = this.gridCellFromEvent(canvas, event, source.colSpan, source.rowSpan);
    const occupant = this.layout.find((item) => item.id !== source.id && this.rectsOverlap(item, cell));
    this.snapshotLayoutForUndo();
    if (occupant) {
      this.swapTilePositions(source.id, occupant.id);
    } else {
      this.layout = this.layout.map((item) => (item.id === source.id ? { ...item, x: cell.x, y: cell.y, customSize: true } : item));
    }
    this.tileDragId = null;
  }

  private swapTilePositions(sourceId: string, targetId: string): void {
    const source = this.layout.find((item) => item.id === sourceId);
    const target = this.layout.find((item) => item.id === targetId);
    if (!source || !target) return;
    this.layout = this.layout.map((item) => {
      if (item.id === source.id) return { ...item, x: target.x, y: target.y, customSize: true };
      if (item.id === target.id) return { ...item, x: source.x, y: source.y, customSize: true };
      return item;
    });
  }

  /** True when two spans (a tile's own x/y/colSpan/rowSpan, or a candidate
   *  drop rectangle of the same shape) overlap on the grid. */
  private rectsOverlap(
    a: { x: number; y: number; colSpan: number; rowSpan: number },
    b: { x: number; y: number; colSpan: number; rowSpan: number },
  ): boolean {
    return a.x < b.x + b.colSpan && a.x + a.colSpan > b.x && a.y < b.y + b.rowSpan && a.y + a.rowSpan > b.y;
  }

  /** Resolves the grid cell (0-based column/row) under a drop's cursor
   *  position, from the canvas's OWN rendered track sizes — reads the actual
   *  computed `grid-template-columns` track width rather than recomputing it
   *  from padding/gap assumptions, so it stays correct if the CSS ever changes.
   *  Clamped so the dragged tile's own span never runs off the 12-column grid
   *  (rows have no upper bound — the grid auto-creates more as needed). */
  private gridCellFromEvent(
    canvas: HTMLElement,
    event: DragEvent,
    colSpan: number,
    rowSpan: number,
  ): { x: number; y: number; colSpan: number; rowSpan: number } {
    const rect = canvas.getBoundingClientRect();
    const styles = getComputedStyle(canvas);
    const paddingLeft = parseFloat(styles.paddingLeft) || 0;
    const paddingTop = parseFloat(styles.paddingTop) || 0;
    const colGap = parseFloat(styles.columnGap) || 14;
    const rowGap = parseFloat(styles.rowGap) || 14;
    const trackWidths = styles.gridTemplateColumns.split(' ').map((track) => parseFloat(track)).filter((n) => Number.isFinite(n));
    const colWidth = trackWidths.length ? trackWidths[0] : (rect.width - paddingLeft * 2 - 11 * colGap) / 12;
    const rowHeight = this.EDIT_GRID_ROW_PX;

    const relX = event.clientX - rect.left - paddingLeft;
    const relY = event.clientY - rect.top - paddingTop + canvas.scrollTop;
    const rawCol = Math.floor(relX / (colWidth + colGap));
    const rawRow = Math.floor(relY / (rowHeight + rowGap));

    return {
      x: this.clamp(rawCol, 0, Math.max(0, 12 - colSpan)),
      y: Math.max(0, rawRow),
      colSpan,
      rowSpan,
    };
  }

  onTileDragEnd(): void {
    this.tileDragId = null;
  }

  startTileResize(id: string, event: MouseEvent, horizontal: 'left' | 'right' | null = 'right', vertical: 'top' | 'bottom' | null = 'bottom'): void {
    if (!this.editMode) return;
    event.preventDefault();
    event.stopPropagation();
    const item = this.layout.find((layoutItem) => layoutItem.id === id);
    if (!item) return;
    this.snapshotLayoutForUndo();
    this.resizeInteraction = {
      id,
      horizontal,
      vertical,
      startX: event.clientX,
      startY: event.clientY,
      startColSpan: item.colSpan,
      startRowSpan: item.rowSpan,
      startGridX: item.x,
      startGridY: item.y,
    };
  }

  /** In-flight drag on a group-card MEMBER's bottom edge (per-member height,
   *  Edit Layout only) — independent of the tile-level resizeInteraction. */
  private memberResizeInteraction: { groupId: string; childId: string; startY: number; startHeight: number } | null = null;

  startMemberResize(groupRec: PublishedWidgetRec, childId: string, event: MouseEvent): void {
    if (!this.editMode) return;
    event.preventDefault();
    event.stopPropagation();
    const host = (event.target as HTMLElement | null)?.closest('.group-card-member') as HTMLElement | null;
    const startHeight = this.memberHeightFor(groupRec.widget, childId) ?? host?.offsetHeight ?? 160;
    this.snapshotLayoutForUndo();
    this.memberResizeInteraction = { groupId: groupRec.id, childId, startY: event.clientY, startHeight };
  }

  /** A member's user-dragged height in px, or null when it auto-sizes.
   *  Stored on the group-card widget itself (`memberHeights[childId]`) so
   *  buildPersistedLayoutJson's widget snapshot persists it with Save Layout. */
  memberHeightFor(groupWidget: any, childId: string): number | null {
    const value = Number(groupWidget?.memberHeights?.[childId]);
    return Number.isFinite(value) && value > 0 ? value : null;
  }

  @HostListener('window:mousemove', ['$event'])
  onWindowMouseMove(event: MouseEvent): void {
    const memberInteraction = this.memberResizeInteraction;
    if (memberInteraction) {
      const rec = this.widgets.find((widget) => widget.id === memberInteraction.groupId);
      if (rec) {
        // Floor keeps a header strip + a sliver of content grabbable — a
        // member dragged to 0 would be impossible to resize back.
        const next = Math.max(48, Math.round(memberInteraction.startHeight + (event.clientY - memberInteraction.startY)));
        rec.widget.memberHeights = { ...(rec.widget.memberHeights ?? {}), [memberInteraction.childId]: next };
      }
      return;
    }
    const interaction = this.resizeInteraction;
    if (!interaction) return;
    const rawColDelta = Math.round((event.clientX - interaction.startX) / 90);
    const rawRowDelta = Math.round((event.clientY - interaction.startY) / this.EDIT_GRID_ROW_PX);
    const colDelta = interaction.horizontal === 'left' ? -rawColDelta : interaction.horizontal === 'right' ? rawColDelta : 0;
    const rowDelta = interaction.vertical === 'top' ? -rawRowDelta : interaction.vertical === 'bottom' ? rawRowDelta : 0;
    this.layout = this.layout.map((item) => {
      if (item.id !== interaction.id) return item;
      const nextColSpan = this.clamp(interaction.startColSpan + colDelta, this.minColSpanForItem(item.id), 12);
      const nextRowSpan = this.clamp(interaction.startRowSpan + rowDelta, this.minRowSpanForItem(item.id), 18);
      // A 'left'/'top' handle grows the tile by moving THAT edge outward — the
      // opposite (right/bottom) edge must stay fixed, so the grid x/y start
      // shifts by exactly the applied (post-clamp) span change, in the
      // opposite direction. 'right'/'bottom' handles only change the span.
      const appliedColGrowth = nextColSpan - interaction.startColSpan;
      const appliedRowGrowth = nextRowSpan - interaction.startRowSpan;
      const nextX = interaction.horizontal === 'left' ? Math.max(0, interaction.startGridX - appliedColGrowth) : item.x;
      const nextY = interaction.vertical === 'top' ? Math.max(0, interaction.startGridY - appliedRowGrowth) : item.y;
      return { ...item, colSpan: nextColSpan, rowSpan: nextRowSpan, x: nextX, y: nextY, customSize: true };
    });
    this.notifyChartsAboutResize();
  }

  @HostListener('window:mouseup')
  onWindowMouseUp(): void {
    this.resizeInteraction = null;
    this.memberResizeInteraction = null;
  }

  isWidgetBusy(widget: any): boolean {
    const id = String(widget?.componentId ?? '');
    return !!id && this.recomputingComponentIds.has(id);
  }

  /** Any recompute in flight — drives the single dashboard-level progress bar,
   *  so the user gets ONE clear "updating" signal instead of reading it off
   *  however many tiles happen to be targeted. */
  get isAnyWidgetBusy(): boolean {
    return this.recomputingComponentIds.size > 0;
  }

  /** Which skeleton shape to draw while a tile first loads, so the placeholder
   *  roughly matches what's about to render (a chart's plot area vs a table's
   *  rows vs a KPI's single figure) instead of one generic block everywhere.
   *  The CSS keys off this via [data-variant]. */
  skeletonVariant(widget: any): string {
    const type = String(widget?.type ?? '').toLowerCase();
    if (type === 'kpi-card') return 'kpi';
    if (type === 'drill-down' || type === 'table-matrix') return 'table';
    if (this.isInputWidget(widget)) return 'input';
    return 'chart';
  }

  // ── Dropdown filter selection persistence (Vertical/Application etc.) ─────
  // sessionStorage-scoped to THIS dashboard's slug, so a viewer's live pick
  // survives a real browser refresh (or the save-triggered reload elsewhere in
  // this component) instead of reverting to `payload.value` — the author's
  // hardcoded default baked in at publish time. Session-scoped on purpose:
  // closing the tab clears it, same as a genuinely fresh visit would; opening
  // a DIFFERENT dashboard uses its own separate key and its own defaults.
  private dropdownStateStorageKey(): string {
    return `dvmDropdownState:${this.slug}`;
  }

  private loadPersistedDropdownState(): Record<string, unknown> {
    try {
      const raw = sessionStorage.getItem(this.dropdownStateStorageKey());
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  }

  private persistDropdownValue(componentId: string, value: unknown): void {
    if (!componentId) return;
    try {
      const store = this.loadPersistedDropdownState();
      const empty = value === null || value === undefined || value === ''
        || (Array.isArray(value) && !value.length);
      if (empty) delete store[componentId]; else store[componentId] = value;
      sessionStorage.setItem(this.dropdownStateStorageKey(), JSON.stringify(store));
    } catch {
      /* sessionStorage unavailable (private mode/quota) — selection just won't survive a refresh */
    }
  }

  /** An EXPLICIT "Reset" (toolbar action, not the silent on-open reset) means
   *  the viewer wants the true published defaults back — drop the recorded
   *  picks so the next open doesn't immediately restore what was just reset. */
  private clearPersistedDropdownState(): void {
    try {
      sessionStorage.removeItem(this.dropdownStateStorageKey());
    } catch {
      /* sessionStorage unavailable — nothing to clear */
    }
  }

  /** A fingerprint of every OTHER dropdown widget's current live value (e.g.
   *  Vertical's), excluding the dropdown whose own options are being
   *  refreshed — this is the "context" a cached option list is scoped to, so
   *  Marine's cached Application options never leak into Mining's. Reads
   *  straight off `this.widgets` (always current by this point) rather than
   *  `activeDropdownFilters`, which the driving dropdown doesn't necessarily
   *  populate on every path that can trigger this refresh. */
  private dropdownContextFingerprint(excludeComponentId: string): string {
    return this.widgets
      .filter((rec) => String(rec.widget?.type ?? '').toLowerCase() === 'dropdown'
        && String(rec.widget?.componentId ?? '') !== excludeComponentId)
      .map((rec) => {
        const value = rec.widget?.payload?.value;
        const values = Array.isArray(value) ? value.map(String) : value == null || value === '' ? [] : [String(value)];
        return `${String(rec.widget?.componentId ?? '')}=${values.sort().join(',')}`;
      })
      .sort()
      .join('|');
  }

  /** Widens (never narrows) a dropdown's option list for a given context —
   *  once an option has legitimately appeared for THIS combination of other
   *  active filters, it's remembered and re-merged in on every later refresh,
   *  even if a subsequent response for the same context comes back narrower. */
  private mergeAndCacheDropdownOptions(componentId: string, context: string, freshOptions: string[]): string[] {
    if (!componentId) return freshOptions;
    try {
      const key = `dvmDropdownOptions:${this.slug}:${componentId}:${context}`;
      const raw = sessionStorage.getItem(key);
      const cached: string[] = raw ? JSON.parse(raw) : [];
      const merged = [...freshOptions];
      cached.forEach((opt) => { if (!merged.includes(opt)) merged.push(opt); });
      sessionStorage.setItem(key, JSON.stringify(merged));
      return merged;
    } catch {
      return freshOptions;
    }
  }

  private loadPublishedDashboard(): void {
    this.cancelPendingAutoPersist();
    this.recomputeGeneration += 1;
    this.renderVersion += 1;
    this.loading = true;
    this.loadError = '';
    this.widgets = [];
    this.layout = [];
    this.landingConfig = null;

    if (!this.slug) {
      this.loading = false;
      this.loadError = 'Dashboard slug is missing.';
      return;
    }

    // ARRIVING STRAIGHT FROM A PUBLISH: render exactly what was just written.
    //
    // dashboard_layouts/dynamic_filter is eventually consistent — for a short
    // window after the write it still serves the PREVIOUS version of the row.
    // Letting it win here is what made a publish look like it hadn't applied
    // until the user refreshed once. The handoff is one-shot and short-lived, so
    // this only affects the single load that follows a publish; a refresh (or
    // any other visit) takes the API path below as normal.
    const justPublished = consumeJustPublishedDashboard<Record<string, unknown>>(this.slug);
    if (justPublished) {
      // Seed the fallback cache too, so a reload during the lag window still has
      // the new version to fall back on if the API errors outright.
      writeDynamicDashboardCache(this.slug, justPublished);
      this.renderLayout(justPublished);
      // Ownership still has to be resolved (it gates Edit Layout / Share in the
      // toolbar) — just don't let it gate the render.
      this.resolveCurrentEmail().then((email) => {
        this.confirmOwnership(email);
        this.loadAvailableViews(email);
      });
      return;
    }

    // Read the cache ONLY if the API fails — decompressing it is a synchronous
    // pure-JS LZ pass over the whole layout, pure waste on a successful load.
    this.resolveCurrentEmail().then((email) => {
      this.confirmOwnership(email);
      this.loadAvailableViews(email);
      this.subs.add(
        this.dashboardService.getPublishedLayoutBySlug(this.slug, email).pipe(
          catchError(() => of(null)),
        ).subscribe((layoutJson) => {
          const resolved = layoutJson
            || readDynamicDashboardCache<Record<string, unknown> | null>(this.slug, null);
          if (!resolved) {
            this.loading = false;
            this.loadError = `No published DVM dashboard found for "${this.slug}".`;
            return;
          }
          if (layoutJson) writeDynamicDashboardCache(this.slug, layoutJson);
          this.renderLayout(resolved);
        }),
      );
    });
  }

  /** Fetch the user's published dashboards for the "Available Views" rail —
   *  once per component (navigating between dashboards inside the rail keeps
   *  the same list). Hidden for shared read-only recipients: the list is the
   *  VIEWER's own dashboards, which is meaningless on a share link. */
  /** The rail is OPTIONAL per project (PUBLISHED_VIEWS_RAIL_ENABLED) and per
   *  dashboard (design `rail.enabled` wins when it is a boolean). */
  private get railEnabled(): boolean {
    if (typeof this.designRailEnabled === 'boolean') return this.designRailEnabled;
    const flag = (environment as any).PUBLISHED_VIEWS_RAIL_ENABLED;
    return flag === true || flag === 'true';
  }

  private loadAvailableViews(email: string): void {
    if (this.availableViewsLoaded || !email || this.isSharedReadonlyView || !this.railEnabled) return;
    this.availableViewsLoaded = true;
    this.subs.add(
      this.dashboardService.fetchOwnedDashboardsFromApi(email).subscribe({
        next: (rows) => { this.availableViews = toAvailableViews(rows); },
        error: () => { /* rail simply stays hidden */ },
      }),
    );
  }

  /** The rail renders only when there is at least one OTHER dashboard to open. */
  get showViewsRail(): boolean {
    if (!this.railEnabled || this.landingConfig || this.isSharedReadonlyView) return false;
    const current = this.slug.toLowerCase();
    return this.availableViews.some((v) => v.slug.toLowerCase() !== current);
  }

  /** Rail card click → open that published dashboard (same route family). */
  onOpenAvailableView(slug: string): void {
    if (slug) this.router.navigate(['/dvm-published', slug]);
  }

  /** A group card whose members are ALL input controls (dropdown / slider /
   *  input box) is a FILTER RAIL, not a chart card: it renders as one flat
   *  tinted panel with the controls stacked (label + control), no nested
   *  member boxes and no duplicated member titles — the reference UI's left
   *  filter column. Charts in the group → the normal boxed group card. */
  isFilterGroupCard(widget: any): boolean {
    if (String(widget?.type ?? '') !== 'group-card') return false;
    const ids: string[] = Array.isArray(widget?.childWidgetIds) ? widget.childWidgetIds : [];
    if (!ids.length) return false;
    return ids.every((id) => {
      const type = String(this.widgetRecById(id)?.widget?.type ?? '').toLowerCase();
      return type === 'dropdown' || type === 'slider' || type === 'input-box';
    });
  }

  /** A filter rail hides the builder's placeholder group title ("Grouped
   *  card") — the reference column has no heading; a REAL title the author
   *  typed still shows. Chart group cards always keep their header. */
  showGroupCardHeader(widget: any): boolean {
    if (!this.isFilterGroupCard(widget)) return true;
    const title = String(widget?.title ?? '').trim();
    return !!title && !/^group(?:ed)?\s*card$/i.test(title);
  }

  private renderLayout(layoutJson: Record<string, unknown>): void {
    ++this.recomputeGeneration;
    const openedVersion = ++this.renderVersion;
    const openedSlug = this.slug;
    this.originalLayoutJson = layoutJson;
    this.dashboardName = String(layoutJson['dashboard-name'] ?? layoutJson['name'] ?? this.slug);
    // Surface the dashboard name in the shared top toolbar (beside the company
    // logo) instead of a tall in-page title block — reclaims the vertical space
    // the old `.dvm-published-toolbar` title row used to take.
    //
    // This used to be suppressed for the WHOLE of project mode, on the grounds
    // that the toolbar's environment-configured company text already acts as
    // the heading. But the two render side by side and say different things
    // (COMPANY_LOGO_TEXT is the product name, this is the dashboard's), so that
    // just left project builds with no dashboard name anywhere. Suppress it
    // only when it would literally repeat the branding text beside it.
    if (this.duplicatesBrandingText(this.dashboardName)) {
      this.dashboardTitleService.clearDashboardTitle();
    } else {
      this.dashboardTitleService.setDashboardTitle(this.dashboardName, 'top');
    }
    // Landing Page template — no pageContent widgets at all; the dedicated
    // landing component renders the `landing` config block instead of the grid.
    if (isLandingPageLayout(layoutJson)) {
      this.landingConfig = extractLandingConfig(layoutJson);
      this.widgets = [];
      this.layout = [];
      this.loading = false;
      this.syncToolbarState();
      return;
    }
    this.landingConfig = null;
    // Published dashboards are opened directly (shared link, new tab, refresh)
    // without ever passing through a builder screen, so the api.utils base-URL
    // override singleton (set by dashboard-builder/chart-builder etc.) is never
    // populated here on its own — leaving save/update endpoints to resolve as
    // bare relative paths against this page's own origin instead of the API
    // host. Apply this layout's own `apiEndpoints` block the same way every
    // builder does, on the first published layout load.
    this.apiConfig.applyFromLayout(layoutJson as unknown as BuilderLayoutConfig);
    const widgets = this.extractWidgets(layoutJson['pageContent']);
    // Route reuse must not carry filters from the previous dashboard. Restore
    // the saved selections before any on-open requests are constructed.
    this.activeDropdownFilters.clear();
    widgets.forEach(({ widget }) => {
      if (String(widget?.type ?? '').toLowerCase() !== 'dropdown') return;
      const value = widget?.payload?.value ?? widget?.payload?.defaultValue;
      const values = Array.isArray(value) ? value.map(String) : value == null || value === '' ? [] : [String(value)];
      const field = String(widget?.payload?.field ?? '');
      if (field && values.length) this.activeDropdownFilters.set(String(widget?.componentId ?? ''), { field, values });
    });
    // Dashboard-level design from layout_json (`dvmCustomization.design`) →
    // scoped `--dvm-design-*` / theme-token vars on this host, plus the
    // per-dashboard "Available Views" rail switch.
    this.applyDashboardDesign(resolveDashboardCustomization(layoutJson, null)?.design);
    // Client-side charts (bar-column) + non-charts are 'ready' immediately; only
    // server-computed charts start in 'loading' pending the on-open recompute.
    // Snapshot-only widgets (table-matrix, file-upload) are skipped by every
    // recompute path (see recomputeChartsOnOpen), so they must be 'ready' too —
    // otherwise nothing ever clears their overlay.
    // Group cards are pure CONTAINERS published with no payload — no recompute
    // path ever resolves them, and on a filtered open (URL filterKey/filterValue)
    // applyIncomingNavigationFilter bumps the generation so the on-open
    // recompute's "not in batch → ready" cleanup never runs either: the card's
    // spinner stuck forever. Its children are their own recs with own statuses.
    this.widgets = widgets.map((rec) => ({
      ...rec,
      status:
        this.isChartWidget(rec.widget) && !this.isClientSideWidget(rec.widget) && !this.isSnapshotOnlyWidget(rec.widget) && !this.isGroupCardWidget(rec.widget)
          ? 'loading'
          : 'ready',
    }));
    this.seedActiveDropdownFiltersFromWidgets();
    // Snapshot client-side widgets' rows so cross-filter can be reversed.
    this.captureCrossFilterOriginals();
    // The layout comes straight from what the server returned (each widget's own
    // `layout.colSpan/rowSpan`, set by a previous saveLayout() publish) — NOT
    // localStorage, so every device/browser sees the same saved arrangement.
    this.layout = this.buildLayoutFromJson(layoutJson, this.widgets);
    this.syncToolbarState();
    // Run the same Reset action used by the toolbar on every open. Keep the
    // dashboard behind its loading state until the default-value compute batch
    // has settled, so saved selections/data never flash before reset finishes.
    this.resetDashboardSelections(() => {
      if (openedVersion !== this.renderVersion || openedSlug !== this.slug) return;
      void this.maybeRecomputeChartsOnOpen(this.recomputeGeneration, () => {
        if (openedVersion === this.renderVersion && openedSlug === this.slug) this.loading = false;
      });
    }, false);
  }

  /** Apply the dashboard-level design as scoped `--dvm-design-*` variables on
   *  this component's host element ONLY — the design can never leak into the
   *  builder, the sidebar, or another dashboard. The SCSS reads each variable
   *  with today's exact hardcoded value as fallback, so no design → no change. */
  private applyDashboardDesign(design: DashboardDesign | undefined): void {
    this.designRailEnabled = typeof design?.rail?.enabled === 'boolean' ? design.rail.enabled : undefined;
    if (this.resolvedEmailMemo) this.loadAvailableViews(this.resolvedEmailMemo);
    const host = this.hostEl?.nativeElement as HTMLElement | undefined;
    if (!host?.style) return;
    for (const name of this.appliedDesignVars) host.style.removeProperty(name);
    const vars = designToCssVars(design);
    this.appliedDesignVars = Object.keys(vars);
    for (const [name, value] of Object.entries(vars)) host.style.setProperty(name, value);
  }

  /** A popup widget's `navigate` button — route to that published dashboard. */
  onPopupNavigate(slug: string): void {
    if (slug) this.router.navigate(['/dvm-published', slug]);
  }

  /** Source-dashboard side: a table tile fired navigation → route to the target
   *  published dashboard, carrying the clicked value as query params so the
   *  target filters its charts on load. */
  onTableNavigate(event: { targetDashboard: string; targetComponents: string[]; filterKey: string; filterValue: string }): void {
    if (!event?.targetDashboard) return;
    this.router.navigate(['/dvm-published', event.targetDashboard], {
      queryParams: {
        filterKey: event.filterKey,
        filterValue: event.filterValue,
        ...(event.targetComponents?.length ? { filterTargets: event.targetComponents.join(',') } : {}),
      },
    });
  }

  /** The table POSTed its own save (custom saveUrl / bulkSaveUrl) and every
   *  request of that batch landed. The component only holds the client's edited
   *  copy - columns the backend DERIVES from the edit (a rule service computing
   *  realized cost from an edited percent, a rule that rewrites rows of OTHER
   *  tables on the screen) only exist server-side. So re-fetch the whole
   *  dashboard's table rows and swap them in. */
  onTableSaved(rec: PublishedWidgetRec): void {
    this.refreshTablesFromServer(rec);
  }

  /** Second-chance re-fetch after a save: the platform commits asynchronously,
   *  so the first re-fetch can still serve the pre-save rows. */
  private savedRefreshTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Re-hit the same `dashboard_layouts/dynamic_filter` call the page loads
   * from (the backend re-hydrates every table's `data.data` from the live
   * dataset) and swap the fresh rows into EVERY table-matrix rec on screen
   * (matched by the deterministic extractWidgets ids). Charts/inputs are left
   * untouched - no re-render storm. If the fetch fails or carries no rows for
   * the edited table, fall back to the single-table dataset refresh.
   *
   * `rec` is the table that was edited: if ITS rows come back byte-identical
   * to what it already shows, the backend hadn't committed yet - re-fetch once
   * more after a short delay (the component's pendingSaves overlay keeps the
   * edited cell from visibly reverting in between).
   */
  private refreshTablesFromServer(rec: PublishedWidgetRec, retried = false): void {
    if (this.savedRefreshTimer) { clearTimeout(this.savedRefreshTimer); this.savedRefreshTimer = null; }
    const before = this.tableRowsSignature(rec.widget);
    this.resolveCurrentEmail().then((email) => {
      this.subs.add(
        this.dashboardService.getPublishedLayoutBySlug(this.slug, email).pipe(
          catchError(() => of(null)),
        ).subscribe((layoutJson) => {
          const fresh = layoutJson ? this.collectTableRowsById(layoutJson['pageContent']) : new Map<string, Array<Record<string, unknown>>>();
          if (!fresh.size || !fresh.has(rec.id)) { this.onTableRefresh(rec); return; }
          this.widgets = this.widgets.map((r) => {
            const rows = fresh.get(r.id);
            if (!rows || !this.isTableMatrixWidget(r.widget)) return r;
            // Update BOTH row copies - see onTableRefresh.
            return { ...r, widget: { ...r.widget, staticData: rows, data: { ...(r.widget?.data ?? {}), data: rows } } };
          });
          const after = JSON.stringify(fresh.get(rec.id));
          if (!retried && after === before) {
            this.savedRefreshTimer = setTimeout(() => { this.savedRefreshTimer = null; this.refreshTablesFromServer(rec, true); }, 2500);
          }
        }),
      );
    });
  }

  /** Stable signature of the rows a table rec currently shows. */
  private tableRowsSignature(widget: any): string {
    const rows = (widget?.data as { data?: unknown } | undefined)?.data ?? widget?.staticData;
    return Array.isArray(rows) ? JSON.stringify(rows) : '';
  }

  /** Walk a freshly served layout's pageContent with the SAME id scheme as
   *  extractWidgets (widgetId/id, deduped with a counter, group-card children
   *  registered right after their parent) and collect every table-matrix
   *  widget's served rows. Only rows the backend actually served count - an
   *  empty payload must never blank a table that has a usable snapshot. */
  private collectTableRowsById(pageContent: unknown): Map<string, Array<Record<string, unknown>>> {
    const out = new Map<string, Array<Record<string, unknown>>>();
    this.walkPublishedTableNodes(pageContent, (id, node) => {
      const served = (node?.data as { data?: unknown } | undefined)?.data;
      if (Array.isArray(served) && served.length) out.set(id, served as Array<Record<string, unknown>>);
    });
    return out;
  }

  /** Visit every table-matrix node of a layout's pageContent with the SAME id
   *  scheme as extractWidgets, so `id` lines up with `this.widgets[i].id`. */
  private walkPublishedTableNodes(pageContent: unknown, visitTable: (id: string, node: any) => void): void {
    const count = new Map<string, number>();
    let total = 0;
    const register = (node: any): void => {
      const base = String(node.widgetId ?? node.id ?? `widget-${total + 1}`).replace(/[^a-zA-Z0-9-_]/g, '-');
      const idx = (count.get(base) ?? 0) + 1;
      count.set(base, idx);
      const id = idx === 1 ? base : `${base}__${idx}`;
      total += 1;
      if (this.isTableMatrixWidget(node)) visitTable(id, node);
    };
    const visit = (node: any): void => {
      if (!node || typeof node !== 'object') return;
      if (node.type) {
        register(node);
        if (String(node.type) === 'group-card' && Array.isArray(node.children)) {
          (node.children as any[]).filter((c) => c && typeof c === 'object').forEach((c) => register(c));
        }
        return;
      }
      if (Array.isArray(node.columns)) node.columns.forEach((child: any) => visit(child));
      if (Array.isArray(node.rows)) node.rows.forEach((child: any) => visit(child));
    };
    if (Array.isArray(pageContent)) pageContent.forEach((row) => visit(row));
  }

  /**
   * `originalLayoutJson` is the layout as it was LOADED. Every later table
   * change on this page - a Save all that the backend acknowledged and the
   * post-save re-fetch swapped in, a Refresh, a file upload, a dropdown-driven
   * recompute - lives only on `this.widgets[i].widget`. Back to Builder used to
   * hand the builder the loaded copy, so an edit saved right here rolled back to
   * the pre-edit rows the moment the builder opened (and, if re-published from
   * there, that stale snapshot went back out). Return a copy of the layout whose
   * table nodes carry the rows (and drill-down tree) currently on screen.
   */
  private layoutWithCurrentTableRows(layoutJson: Record<string, unknown>): Record<string, unknown> {
    const byId = new Map(this.widgets.map((rec) => [rec.id, rec.widget as Record<string, unknown>]));
    let clone: Record<string, unknown>;
    try { clone = JSON.parse(JSON.stringify(layoutJson)) as Record<string, unknown>; } catch { return layoutJson; }
    this.walkPublishedTableNodes(clone['pageContent'], (id, node) => {
      const current = byId.get(id);
      if (!current) return;
      const rows = current['staticData'];
      if (Array.isArray(rows) && rows.length) {
        node.staticData = rows;
        node.data = { ...((node.data as Record<string, unknown>) ?? {}), data: rows };
      }
      if (current['drilldownData'] && typeof current['drilldownData'] === 'object') node.drilldownData = current['drilldownData'];
    });
    return clone;
  }

  /** A Normal/Editable table requested a refresh - re-fetch its dataset rows and
   *  swap them into the widget so the table re-renders with fresh data. */
  onTableRefresh(rec: PublishedWidgetRec): void {
    this.refreshWidgetDataset(rec);
  }

  /** Re-fetch ONE widget's dataset rows and swap them into the live widget
   *  (both `staticData` and `data.data` — see the note below on why both). */
  private refreshWidgetDataset(rec: PublishedWidgetRec): void {
    const w: any = rec?.widget || {};
    const caps = (w.capabilities || {}) as Record<string, unknown>;
    const dataset = String(caps['dataset'] || w?.payload?.dataset || w?.dataset || '').trim();
    if (!dataset) return;
    const url = buildSelfServiceApiUrl(`/${encodeURIComponent(dataset)}/dynamic_filter`);
    this.subs.add(
      this.http.post<unknown>(url, { uuid: null }).subscribe({
        next: (res) => {
          const rows = ((res as { data?: unknown })?.data ?? res) as Array<Record<string, unknown>>;
          if (!Array.isArray(rows)) return;
          this.widgets = this.widgets.map((r) => r.id === rec.id
            // Update BOTH row copies. `data.data` is what a later Save Layout
            // persists and what withFreshTableSnapshot promotes on the next
            // load — leaving it behind here would re-surface these same stale
            // rows after a reload, which is the bug this pairs with.
            ? { ...r, widget: { ...r.widget, staticData: rows, data: { ...(r.widget?.data ?? {}), data: rows } } }
            : r);
        },
        error: () => { /* keep current rows */ },
      }),
    );
  }

  /** A save completed somewhere on the dashboard — refresh EVERY tile/filter,
   *  not just the one that saved, and with NO browser reload.
   *
   *  A per-widget dataset re-fetch (`refreshWidgetDataset`) only ever covers
   *  table-matrix tiles — bar/line/KPI/scatter charts and dropdown FILTER
   *  widgets are driven by an entirely different type-dispatched recompute
   *  pipeline (`recomputeChartsOnOpen`) that only ever runs when the dashboard
   *  document itself is (re)loaded. So the correct "refresh everything" is to
   *  re-run that same load pipeline — `loadPublishedDashboard` re-fetches the
   *  layout_json (server-hydrated with fresh table rows) and re-renders it,
   *  which rebuilds every widget type exactly like opening the dashboard fresh
   *  does, filters included — just an in-app re-fetch, not `location.reload`. */
  refreshAllTiles(): void {
    this.loadPublishedDashboard();
  }

  /** A Normal/Editable table emitted a drag-reordered column order. Persist the
   *  order into the live widget, so published Save Layout / auto-persist writes
   *  it back into layout_json instead of losing it on refresh. */
  onTableColumnReorder(rec: PublishedWidgetRec, order: string[]): void {
    if (!rec?.widget || !Array.isArray(order) || !order.length) return;
    const nextWidget = this.withReorderedTableColumns(rec.widget, order);
    this.widgets = this.widgets.map((item) => {
      if (item.id === rec.id) return { ...item, widget: nextWidget };
      if (String(item.widget?.type ?? '') !== 'group-card' || !Array.isArray(item.widget?.children)) return item;
      return {
        ...item,
        widget: {
          ...item.widget,
          children: item.widget.children.map((child: any) =>
            String(child?.widgetId ?? child?.id ?? '') === rec.id ||
            String(child?.componentId ?? '') === String(rec.widget?.componentId ?? '')
              ? nextWidget
              : child,
          ),
        },
      };
    });
    if (!this.isSharedReadonlyView && this.isOwner) {
      this.scheduleAutoPersistOverride();
    }
  }

  private withReorderedTableColumns(widget: any, order: string[]): any {
    const caps = { ...((widget?.capabilities ?? {}) as Record<string, unknown>) };
    const existing = (Array.isArray(caps['columns']) ? caps['columns'] : []) as Array<Record<string, unknown>>;
    const byField = new Map(existing.map((column) => [String(column['field'] ?? ''), column]));
    const reordered = order.map((field) => byField.get(field) || { field });
    const mentioned = new Set(reordered.map((column) => String(column['field'] ?? '')));
    caps['columns'] = [
      ...reordered,
      ...existing.filter((column) => !mentioned.has(String(column['field'] ?? ''))),
    ];

    const payload = { ...(widget?.payload ?? {}) };
    if (Array.isArray(payload['columns']) && (payload['columns'] as unknown[]).length) {
      const entries = payload['columns'] as unknown[];
      const fieldOf = (entry: unknown): string =>
        entry && typeof entry === 'object'
          ? String((entry as Record<string, unknown>)['field'] ?? '')
          : String(entry ?? '');
      const payloadByField = new Map(entries.map((entry) => [fieldOf(entry), entry]));
      const orderedPayload = order.filter((field) => payloadByField.has(field)).map((field) => payloadByField.get(field));
      const orderedSet = new Set(order);
      payload['columns'] = [
        ...orderedPayload,
        ...entries.filter((entry) => !orderedSet.has(fieldOf(entry))),
      ];
    }
    if (payload['capabilities'] && typeof payload['capabilities'] === 'object') {
      payload['capabilities'] = { ...(payload['capabilities'] as Record<string, unknown>), columns: caps['columns'] };
    }

    return this.normalizePublishedWidget({
      ...widget,
      capabilities: caps,
      payload,
    });
  }

  /** Editable table Save All → persist the edited rows through the platform
   *  `dashboard_layouts/dynamic_filter` API. If the tile explicitly configures a
   *  custom save endpoint that wins; otherwise Save all posts the edited values
   *  to dashboard_layouts/dynamic_filter (the standard persistence API). */
  onTableSave(rec: PublishedWidgetRec, event: TableMatrixSaveEvent): void {
    // Drill-down cell edits: fold the saved changes into the widget's STORED
    // tree first, so any later re-render from the widget data keeps the saved
    // values (lib-drill-down only edited its own clone).
    const widget = rec.widget as Record<string, unknown> | undefined;
    const drill = (widget?.['drilldownData']
      ?? (widget?.['capabilities'] as Record<string, unknown> | undefined)?.['drilldownData']) as Record<string, unknown> | undefined;
    const isDrilldownSave = !!drill && (event?.rows ?? []).some((r) => Array.isArray(r?.values?.['path']));
    if (isDrilldownSave) applyDrillDownSaveToWidgetData(drill, event);
    const customUrl = (event?.saveUrl || '').trim();
    const target = customUrl
      ? buildSelfServiceApiUrl(customUrl.startsWith('/') ? customUrl : `/${customUrl}`)
      : buildSelfServiceApiUrl('/dashboard_layouts/dynamic_filter');
    // Resolve the current user's email (same helper publish/save use), then POST
    // an envelope carrying the edited rows (rowId + state + values) so the backend
    // can apply the create/update/delete for each changed record.
    this.resolveCurrentEmail().then((email) => {
      const payload = {
        email_id: [email],
        dashboard_id: this.dashboardName || this.slug,
        componentId: event.componentId,
        dataset: event.dataset,
        operation: event.operation,
        transactional: event.transactional === true,
        rows: event.rows,
      };
      this.subs.add(
        this.http.post<unknown>(target, payload).subscribe({
          // Drill-down: SKIP the post-save refresh — a re-compute would
          // instantly overwrite the just-saved values with backend truth,
          // which reverts them until the backend actually persists this
          // contract. The widget already renders the saved values.
          // Refresh every dataset-backed tile, not just this one — a saved
          // record can affect other tiles reading the same/related dataset.
          next: () => { this.ui.showSuccess('Changes saved.'); if (!isDrilldownSave) this.refreshAllTiles(); },
          error: () => this.ui.showError('Save failed — please try again.'),
        }),
      );
    });
  }

  /** Editable table workflow action. The component POSTs to a configured
   *  workflowUrl itself; this parent handler is the emit fallback (no URL set)
   *  so the action is surfaced rather than silently dropped. */
  onTableWorkflow(_rec: PublishedWidgetRec, event: TableMatrixWorkflowEvent): void {
    if (event?.workflowUrl) return; // component already POSTed
    console.info('[dvm-table] workflow action (no endpoint configured):', event);
    this.ui.showSuccess(`Workflow "${event.action}" on ${event.rowIds.length} row(s) — configure a Workflow endpoint to submit.`);
  }

  /** A `button`/`hyperlink` cell control was actioned in an editable table. */
  onTableCellAction(_rec: PublishedWidgetRec, event: TableMatrixCellActionEvent): void {
    console.info('[dvm-table] cell action:', event);
  }

  /** Target-dashboard side: if the URL carried a navigation filter, push it into
   *  the target components' compute payloads and recompute them — reusing the
   *  same machinery an input control (slider/dropdown) uses to drive targets.
   *  Empty target list = ALL chart widgets on this dashboard. */
  private applyIncomingNavigationFilter(): void {
    const nav = this.incomingNavFilter;
    if (!nav) return;
    // Which widgets to filter: the named targets, or every chart widget.
    const targetSet = new Set(nav.targetComponents);
    const targetRecs = this.widgets.filter((rec) => {
      if (!this.isChartWidget(rec.widget) || this.isTableMatrixWidget(rec.widget) || !rec.widget?.payload) return false;
      if (!targetSet.size) return true;
      return targetSet.has(String(rec.widget?.componentId ?? '').trim());
    });
    if (!targetRecs.length) return;

    const targetIds = targetRecs.map((rec) => String(rec.widget?.componentId ?? '').trim()).filter(Boolean);
    this.recomputingComponentIds = new Set(targetIds);
    // Bumping the generation below aborts maybeRecomputeChartsOnOpen — including
    // its "loading but not in the batch → ready" cleanup. Run the equivalent
    // cleanup here, or any widget renderLayout marked 'loading' that has no
    // payload (so it can't be a nav target) keeps its spinner forever.
    const targetRecIds = new Set(targetRecs.map((rec) => rec.id));
    this.widgets = this.widgets.map((rec) => {
      if (targetRecIds.has(rec.id)) return { ...rec, status: 'loading' };
      return rec.status === 'loading' ? { ...rec, status: 'ready' } : rec;
    });
    const generation = ++this.recomputeGeneration;

    // Each target recomputes with the filter injected into ITS OWN payload — the
    // backend receives the extra filterKey/filterValue (+ filters[]) alongside
    // the chart's normal compute payload and returns the scoped result.
    const requests = targetRecs.map((rec) => {
      const payload = {
        ...(rec.widget.payload ?? {}),
        filterKey: nav.filterKey,
        filterValue: nav.filterValue,
        filters: [{ column: nav.filterKey, value: nav.filterValue }],
      };
      rec.widget.payload = payload;
      return this.http.post<Record<string, unknown>>(this.computeUrlForWidget(rec.widget), payload).pipe(
        map((response) => ({ id: rec.id, widget: rec.widget, response, error: null as unknown })),
        catchError((error) => of({ id: rec.id, widget: rec.widget, response: null as Record<string, unknown> | null, error })),
      );
    });

    this.subs.add(
      forkJoin(requests).pipe(
        finalize(() => { if (generation === this.recomputeGeneration) this.recomputingComponentIds = new Set(); }),
      ).subscribe((results) => {
        if (generation !== this.recomputeGeneration) return;
        results.forEach(({ id, widget, response, error }) => {
          if (response && this.applyComputeResultToWidget(id, widget, response)) {
            this.setWidgetStatus(id, 'ready');
            return;
          }
          this.setWidgetStatus(id, error ? 'error' : 'ready', error ? 'Navigation filter compute failed; showing published data.' : undefined);
        });
      }),
    );
  }

  /** `publishedView.computeOnOpen` (dvm-layout-builder.json) gates whether
   *  charts re-run their compute payload on every open, or just show the
   *  published snapshot as-is. Cached after first read (same setting for the
   *  whole session). Defaults to true (the historical always-recompute
   *  behavior) only if the config is unreachable / doesn't say otherwise. */
  private computeOnOpenCache: boolean | null = null;
  private async shouldRecomputeOnOpen(): Promise<boolean> {
    const hasLiveServerChart = this.widgets.some(
      (rec) => this.isChartWidget(rec.widget) && !this.isClientSideWidget(rec.widget) && !this.isSnapshotOnlyWidget(rec.widget) && !!rec.widget?.payload
    );
    if (hasLiveServerChart) return true;
    if (this.computeOnOpenCache !== null) return this.computeOnOpenCache;
    try {
      const config = await firstValueFrom(this.http.get<Record<string, unknown>>('/assets/self-service-config/dvm-layout-builder.json'));
      const publishedView = config?.['publishedView'] as Record<string, unknown> | undefined;
      this.computeOnOpenCache = publishedView?.['computeOnOpen'] !== false;
    } catch {
      this.computeOnOpenCache = true;
    }
    return this.computeOnOpenCache;
  }

  private async maybeRecomputeChartsOnOpen(generation: number, onComplete?: () => void): Promise<void> {
    const shouldRecompute = await this.shouldRecomputeOnOpen();
    if (generation !== this.recomputeGeneration) return; // a newer load superseded this one
    if (!shouldRecompute) {
      // Snapshot from dynamic_filter is the source of truth — no recompute will
      // run, so flip every tile renderLayout marked 'loading' to 'ready' here,
      // otherwise their busy overlays never clear.
      this.widgets = this.widgets.map((rec) => (rec.status === 'loading' ? { ...rec, status: 'ready' } : rec));
      onComplete?.();
      return;
    }
    // Same group-card exclusion as renderLayout: containers are never computed,
    // so they must never re-enter 'loading' here either.
    this.widgets = this.widgets.map((rec) => (this.isChartWidget(rec.widget) && !this.isSnapshotOnlyWidget(rec.widget) && !this.isGroupCardWidget(rec.widget) ? { ...rec, status: 'loading' } : rec));
    this.recomputeChartsOnOpen(onComplete);
  }

  /**
   * A table-matrix tile carries its rows TWICE: `staticData` (the snapshot
   * frozen at publish time) and `data.data` (which the backend re-hydrates from
   * the live dataset when it serves layout_json). The table renders `staticData`
   * first, so after a cell edit was saved a reload showed the OLD value even
   * though the response already carried the new one.
   *
   * At LOAD ONLY, promote the served rows over the frozen snapshot so the two
   * agree. Deliberately not done inside normalizePublishedWidget: the recompute
   * path re-normalises with a fresh `staticData` patch, and hydrating there
   * would overwrite those newer rows with this stale copy.
   */
  private withFreshTableSnapshot(node: any): any {
    if (!this.isTableMatrixWidget(node)) return node;
    const served = (node?.data as { data?: unknown } | undefined)?.data;
    // Only when the backend actually served rows — an empty array must never
    // blank a table that has a usable snapshot.
    if (!Array.isArray(served) || !served.length) return node;
    return { ...node, staticData: served };
  }

  private extractWidgets(pageContent: unknown): Array<{ id: string; widget: any }> {
    const out: Array<{ id: string; widget: any }> = [];
    const count = new Map<string, number>();
    const register = (node: any): { id: string; widget: any } => {
      const base = String(node.widgetId ?? node.id ?? `widget-${out.length + 1}`).replace(/[^a-zA-Z0-9-_]/g, '-');
      const idx = (count.get(base) ?? 0) + 1;
      count.set(base, idx);
      const id = idx === 1 ? base : `${base}__${idx}`;
      let widget = this.normalizePublishedWidget(this.withFreshTableSnapshot({ ...node }));
      // The dynamic_filter response hydrates live rows under `data.data`, but
      // drill-down widgets render from their separate baked `drilldownData` tree.
      // Rebuild that tree at load time, otherwise new rows/attributes (for
      // example voice_of_business = "comrade") remain invisible even though
      // they are present in the API response.
      const tableType = String(widget?.capabilities?.tableType ?? widget?.tableType ?? '').toLowerCase();
      const isDrilldown = tableType === 'drilldown'
        || widget?.capabilities?.typeFlags?.drilldown === true
        || widget?.typeFlags?.drilldown === true;
      const servedRows = (widget?.data as { data?: unknown } | undefined)?.data;
      if (isDrilldown && Array.isArray(servedRows) && servedRows.length) {
        widget = {
          ...widget,
          drilldownData: this.rebuildPublishedDrilldownData(id, widget, { data: servedRows }),
        };
      }
      if (this.isInputWidget(widget)) {
        widget.onWidgetValueChange = (value: unknown) => this.onInputWidgetChange(widget, value);
      }
      const entry = { id, widget };
      out.push(entry);
      return entry;
    };
    const visit = (node: any): void => {
      if (!node || typeof node !== 'object') return;
      if (node.type) {
        const entry = register(node);
        // A 'group-card' widget's children flatten into the SAME list `out` — as
        // their own independent PublishedWidgetRec entries — so every existing
        // compute/recompute/target-driving path (all keyed by componentId over
        // a flat `this.widgets`) works on a grouped chart exactly like a
        // top-level one. They never get a `this.layout` grid entry of their own
        // (buildLayoutFromJson only reads the outer layout.items snapshot, one
        // per TOP-LEVEL tile), so viewItems naturally never renders them a
        // second time — only the group card's own nested loop does, via
        // `childWidgetIds` + widgetRecById().
        if (String(node.type) === 'group-card' && Array.isArray(node.children)) {
          entry.widget.childWidgetIds = (node.children as any[])
            .filter((child) => child && typeof child === 'object')
            .map((child) => register(child).id);
        }
        return;
      }
      if (Array.isArray(node.columns)) node.columns.forEach((child: any) => visit(child));
      if (Array.isArray(node.rows)) node.rows.forEach((child: any) => visit(child));
    };
    if (Array.isArray(pageContent)) pageContent.forEach((row) => visit(row));
    return out;
  }

  /** Looks up a (possibly nested, group-card child) widget record by id — used
   *  by the template to render a group's members via the same widgetBody
   *  ng-template the top-level tiles use. */
  widgetRecById(id: string): PublishedWidgetRec | undefined {
    return this.widgets.find((w) => w.id === id);
  }

  /** Group members that are input CONTROLS (slider / input box / dropdown /
   *  file upload / popup trigger) render compact — content height only, no
   *  160px chart floor — so a group of inputs fits its card without the
   *  members list scrolling. A popup's trigger button is ~36px tall; the chart
   *  floor padded it with ~120px of dead space. */
  isCompactGroupMember(widget: any): boolean {
    const type = String(widget?.type ?? '').toLowerCase();
    return type === 'slider' || type === 'input-box' || type === 'dropdown' || type === 'file-upload' || type === 'popup';
  }

  private normalizePublishedWidget(widget: any): any {
    const formattedWidget = this.withApiFormatFields(widget);
    const normType = String(formattedWidget?.type ?? '').toLowerCase();
    if (formattedWidget && (normType === 'bar-column' || normType === 'line-area' || normType === 'scatter-bubble')) {
      // The builder persists a fixed pixel height, but the published edit grid
      // sizes tiles at rowSpan x 48px plus row gaps — the chart
      // always came up short, leaving a dead band under it. The tile (with its
      // card header) is the size authority here, so let the chart fill it.
      return { ...formattedWidget, height: '100%' };
    }
    if (!formattedWidget || String(formattedWidget?.type ?? '').toLowerCase() !== 'waterfall-chart') return formattedWidget;
    const bars = Array.isArray(formattedWidget.waterfallBars) ? formattedWidget.waterfallBars : null;
    // The template's waterfall-card-header is the ONE header — always suppress
    // the component's internal card-header, even for a data-less widget (a
    // corrupted/legacy one otherwise rendered BOTH: the double-title bug).
    if (!bars && (!formattedWidget.option || typeof formattedWidget.option !== 'object')) {
      return { ...formattedWidget, hideCardHeader: true };
    }

    const format = formattedWidget.format as Record<string, unknown> | undefined;
    const sourceOption = (bars ? buildWaterfallOption(bars, format) : formattedWidget.option) as EChartsOption;
    const option = { ...(sourceOption as Record<string, unknown>) };
    delete option['title'];

    return {
      ...formattedWidget,
      hideCardHeader: true,
      option: {
        ...option,
        grid: this.withWaterfallTitleGridTop(sourceOption.grid),
        xAxis: this.withWaterfallXAxisLabelRotation(sourceOption.xAxis, format),
      },
    };
  }

  private withApiFormatFields(widget: any): any {
    const format = widget?.format as Record<string, unknown> | undefined;
    if (!format || typeof format !== 'object') return widget;

    const type = String(widget?.type ?? '').toLowerCase();
    const next = { ...widget };
    const formattedTitle = String(format['title'] ?? '').trim();
    if (formattedTitle) next.title = formattedTitle;

    if (type === 'kpi-card' || type === 'horizontal-bar-chart') {
      next.numberFormat = this.toKpiNumberFormat(format);
      next.numberScale = this.toNumberScale(format['numberFormat']);
      next.decimals = Number(format['decimals'] ?? next.decimals ?? 1);
    }

    if (type === 'kpi-card') {
      next.prefix = String(format['prefix'] ?? '');
      next.suffix = String(format['suffix'] ?? '');
      next.infoText = String(format['infoText'] ?? '');
      next.infoIconPosition = String(format['infoIconPosition'] ?? 'corner');
      next.alignment = String(format['contentAlignment'] ?? next.alignment ?? 'center');
      next.cardLayout = String(format['cardLayout'] ?? next.cardLayout ?? '');
      next.titleFontSize = format['titleFontSize'] ?? next.titleFontSize ?? '';
      next.valueFontSize = format['valueFontSize'] ?? next.valueFontSize ?? '';
      next.icon = String(format['icon'] ?? next.icon ?? '');
      next.iconColor = String(format['iconColor'] ?? next.iconColor ?? '');
      next.iconSize = format['iconSize'] ?? next.iconSize ?? '';
      next.subText = String(format['subText'] ?? next.subText ?? '');
      next.deltaText = String(format['deltaText'] ?? next.deltaText ?? '');
      next.deltaDirection = String(format['deltaDirection'] ?? next.deltaDirection ?? 'up');
      next.deltaColor = String(format['deltaColor'] ?? next.deltaColor ?? '');
    }

    if (type === 'horizontal-bar-chart') {
      const positiveColor = String(format['positiveColor'] ?? '').trim();
      if (positiveColor) {
        next.color = positiveColor;
        next.barColor = positiveColor;
      }
      next.showValues = format['showValues'] !== false;
      next.valueLabelPosition = format['showValues'] === false ? 'none' : (next.valueLabelPosition || 'inside');
    }

    return next;
  }

  private toKpiNumberFormat(format?: Record<string, unknown>): string {
    const selected = String(format?.['numberFormat'] ?? 'NONE');
    if (selected === 'THOUSANDS') return 'thousands';
    if (selected === 'PERCENT') return 'percent';
    return 'plain';
  }

  private toNumberScale(value: unknown): string {
    switch (String(value ?? 'NONE')) {
      case 'COMPACT': return 'compact';
      case 'SCALE_THOUSANDS': return 'thousands';
      case 'MILLIONS': return 'millions';
      case 'BILLIONS': return 'billions';
      default: return 'none';
    }
  }

  private withWaterfallTitleGridTop(grid: EChartsOption['grid']): EChartsOption['grid'] {
    const addTop = (item: unknown): unknown => ({ ...(item as Record<string, unknown> | undefined), top: Math.max(Number((item as any)?.top ?? 32), 32) });
    return Array.isArray(grid) ? grid.map(addTop) as EChartsOption['grid'] : addTop(grid) as EChartsOption['grid'];
  }

  private withWaterfallXAxisLabelRotation(xAxis: EChartsOption['xAxis'], format?: Record<string, unknown>): EChartsOption['xAxis'] {
    const rotate = this.toAxisLabelRotation(format?.['xAxisLabelRotation'], 30);
    const patchAxis = (axis: unknown): unknown => ({
      ...((axis as Record<string, unknown> | undefined) || {}),
      axisLabel: {
        ...(((axis as Record<string, unknown> | undefined)?.['axisLabel'] as Record<string, unknown> | undefined) || {}),
        rotate,
      },
    });
    return Array.isArray(xAxis) ? xAxis.map(patchAxis) as EChartsOption['xAxis'] : patchAxis(xAxis) as EChartsOption['xAxis'];
  }

  private toAxisLabelRotation(value: unknown, fallback: number): number {
    const rotation = Number(value ?? fallback);
    return Number.isFinite(rotation) ? rotation : fallback;
  }
  private recomputeChartsOnOpen(onComplete?: () => void): void {
    const generation = this.recomputeGeneration;
    const chartRecords = this.widgets.filter(
      (rec) => this.isChartWidget(rec.widget) && !this.isClientSideWidget(rec.widget) && !this.isSnapshotOnlyWidget(rec.widget) && rec.widget?.payload,
    );
    // Anything marked 'loading' that ISN'T in the recompute batch (e.g. a chart
    // widget published without a payload) would keep its overlay forever —
    // flip those to 'ready' now; they render their snapshot as-is.
    const batchIds = new Set(chartRecords.map((rec) => rec.id));
    this.widgets = this.widgets.map((rec) => (rec.status === 'loading' && !batchIds.has(rec.id) ? { ...rec, status: 'ready' } : rec));
    if (!chartRecords.length) {
      this.reapplyActiveInputOverrides(generation);
      onComplete?.();
      return;
    }

    // Apply each chart AS IT LANDS. forkJoin held every tile blank until the
    // SLOWEST request finished, which is why a dashboard felt slow to load even
    // though each individual compute returned quickly.
    let pending = chartRecords.length;
    const finish = () => {
      if (--pending > 0) return;
      // Base KPIs/charts above just refreshed straight from the dataset, with
      // no knowledge of any slider/input the user had previously dialed in —
      // that would silently reset an active override back to its plain
      // dataset value (the exact regression a `computeOnOpen` comment above
      // already warns about). Replay each active input's own compute call
      // AFTER the fresh base is in place, so its formula re-applies on top of
      // the (possibly changed) current data instead of being lost.
      this.reapplyActiveInputOverrides(generation);
      onComplete?.();
    };

    chartRecords.forEach((rec) => {
      const payload = {
        ...rec.widget.payload,
        filters: this.activeDropdownFilterPayload(),
        otherInputs: this.otherActiveInputValues(String(rec.widget?.componentId ?? '')),
      };
      // `payload` above (fresh filters/otherInputs) was being built and then
      // discarded — computePayloadForWidget(rec.widget) re-read rec.widget's
      // OWN stored payload instead, which could still carry a stale filter
      // snapshot from before the dashboard was reopened. Route it through the
      // fresh payload so a client-mode drill-down table still gets its column
      // list built (computePayloadForWidget's job) but from current filters.
      this.subs.add(
        this.http.post<Record<string, unknown>>(this.computeUrlForWidget(rec.widget), this.computePayloadForWidget({ ...rec.widget, payload })).pipe(
          map((response) => ({ response, error: null as unknown })),
          catchError((error) => of({ response: null, error })),
        ).subscribe(({ response, error }) => {
          if (generation !== this.recomputeGeneration) return;
          if (response) {
            // Refresh the view without rewriting the saved layout.
            const applied = this.applyComputeResultToWidget(rec.id, rec.widget, response);
            if (applied) {
              this.setWidgetStatus(rec.id, 'ready');
              finish();
              return;
            }
          }
          if (error) {
            console.warn('[dvm-published] fresh compute failed for', rec.widget?.componentId ?? rec.id, this.computeUrlForWidget(rec.widget), error);
          }
          this.setWidgetStatus(rec.id, error ? 'error' : 'ready', error ? `Fresh compute failed (${this.describeComputeFailure(error)}); showing published snapshot.` : undefined);
          finish();
        }),
      );
    });
  }

  /** Slider/input widgets whose value has actually been moved away from its
   *  default carry an intentional user override — replay their compute call
   *  (same POST `onInputWidgetChange` makes) so the override's formula lands
   *  on top of whatever the base dataset refresh just produced in this view. Widgets left at their default aren't "overridden",
   *  so there's nothing to replay for them. */
  private reapplyActiveInputOverrides(generation: number): void {
    const inputRecs = this.widgets.filter((rec) => this.isInputWidget(rec.widget) && rec.widget?.payload);
    const active = inputRecs.filter((rec) => {
      // Text/array dropdown values cannot pass the numeric override check.
      // Replay even default selections: rules may have changed since publish.
      if (String(rec.widget?.type ?? '').toLowerCase() === 'dropdown') {
        return this.hasMeaningfulInputValue(rec.widget.payload?.value ?? rec.widget.payload?.defaultValue);
      }
      const value = Number(rec.widget.payload?.value);
      const defaultValue = Number(rec.widget.payload?.defaultValue ?? 0);
      return Number.isFinite(value) && value !== defaultValue;
    });
    if (!active.length) return;

    const requests = active.map((rec) => {
      const payload = {
        ...rec.widget.payload,
        value: rec.widget.payload?.value ?? rec.widget.payload?.defaultValue,
        filters: this.activeDropdownFilterPayload(),
        otherInputs: this.otherActiveInputValues(String(rec.widget?.componentId ?? '')),
      };
      return this.resolveInputComputeCall(rec.widget?.apiConfig, this.computeUrlForWidget(rec.widget), payload, {
        value: payload.value,
        componentId: rec.widget?.componentId,
        field: payload.field,
      }).pipe(
        map((response) => ({ rec, response, error: null as unknown })),
        catchError((error) => of({ rec, response: null, error })),
      );
    });

    this.subs.add(
      forkJoin(requests).subscribe((results) => {
        if (generation !== this.recomputeGeneration) return;
        results.forEach(({ rec, response, error }) => {
          if (response) {
            this.applyRecomputeResults(response, this.extractTargetIds(rec.widget.payload?.targets));
          } else if (error) {
            console.warn('[dvm-published] reapplying input override for', rec.widget?.componentId, 'failed:', error);
          }
        });
      }),
    );
  }

  /** Every OTHER input control currently on the dashboard, with its live value —
   *  bundled into every single-input recompute call so a target/KPI formula
   *  referencing another input's [Token] (see `references` in the design-time
   *  reference rows) resolves against what that input is showing RIGHT NOW,
   *  not a value the backend may have cached from an earlier call. Keyed by
   *  componentId (unambiguous) and title (matches the reference metadata's
   *  typed `inputTitle`, so the backend can match either way). */
  private otherActiveInputValues(excludeComponentId: string): Array<{ componentId: string; title: string; value: unknown }> {
    return this.widgets
      .filter((rec) => this.isInputWidget(rec.widget) && String(rec.widget?.componentId ?? '') !== String(excludeComponentId))
      .map((rec) => ({
        componentId: String(rec.widget?.componentId ?? ''),
        title: String(rec.widget?.title ?? ''),
        value: rec.widget?.payload?.value ?? rec.widget?.payload?.defaultValue ?? null,
      }))
      .filter((entry) => !!entry.componentId && this.hasMeaningfulInputValue(entry.value));
  }

  private hasMeaningfulInputValue(value: unknown): boolean {
    return value !== null && value !== undefined && value !== ''
      && !(Array.isArray(value) && value.length === 0);
  }

  /** SSA $ resolves Price Inflation %/Cost Inflation %'s own cross-referenced
   *  values (DN Price, Total Cost, ...) from whatever the backend last cached
   *  for THEM - but every slider's compute call is independent, so moving SSA
   *  $ alone never refreshes that cache unless Price Inflation %/Cost
   *  Inflation % have ALSO been (re-)submitted since. Replaying their own
   *  compute call first - with whatever value they currently show, even their
   *  default/zero - keeps every cross-reference SSA $ depends on fresh,
   *  matching what the Waterfall itself would show if those sliders were
   *  touched directly. */
  private sideEffectInputsFor(widget: any): Array<{ widget: any }> {
    const title = String(widget?.title ?? '').trim().toLowerCase();
    if (title !== 'ssa $') return [];
    const wanted = new Set(['price inflation %', 'cost inflation %']);
    return this.widgets
      .filter((rec) => this.isInputWidget(rec.widget)
        && String(rec.widget?.componentId ?? '') !== String(widget?.componentId ?? '')
        && wanted.has(String(rec.widget?.title ?? '').trim().toLowerCase()))
      .map((rec) => ({ widget: rec.widget }));
  }

  onInputWidgetChange(widget: any, rawValue: unknown): void {
    const value = rawValue && typeof rawValue === 'object' && 'end' in (rawValue as Record<string, unknown>)
      ? (rawValue as Record<string, unknown>)['end']
      : rawValue;
    const payload = {
      ...(widget?.payload ?? {}),
      value,
      filters: this.activeDropdownFilterPayload(),
    };
    if (!this.hasMeaningfulInputValue(value)) {
      delete (payload as Record<string, unknown>)['value'];
    }

    // A dropdown should honor only the targets explicitly configured in the
    // builder. Rebuilding from every dashboard widget re-adds tables/charts the
    // user intentionally removed from the dropdown target list.
    if (String(widget?.type ?? '').toLowerCase() === 'dropdown') {
      // The client-side family (bar-column / line-area / scatter-bubble) has no
      // server recompute (buildRecomputedWidgetFields returns null for them) —
      // filter their staticData locally from the captured originals instead,
      // composing across several dropdowns.
      const dropdownField = String(payload['field'] ?? '');
      const dropdownValues = Array.isArray(value) ? value.map(String) : value == null || value === '' ? [] : [String(value)];
      const currentComponentId = String(widget?.componentId ?? '');
      const configured = this.configuredDropdownTargetIds(payload);
      // A parent dropdown can target another dropdown. Its old selection is
      // invalid until the new option list arrives, so remove it from both the
      // rendered control and the filters sent to compute.
      this.widgets = this.widgets.map((rec) => {
        const id = String(rec.widget?.componentId ?? '');
        if (id === currentComponentId || !configured.has(id) || String(rec.widget?.type ?? '').toLowerCase() !== 'dropdown') return rec;
        this.activeDropdownFilters.delete(id);
        return {
          ...rec,
          widget: {
            ...rec.widget,
            value: [],
            payload: { ...(rec.widget?.payload ?? {}), value: [] },
          },
        };
      });
      // Keep the latest selection for a field only. This removes stale values
      // from copied/previous dropdown instances without rebuilding the other
      // active fields from a potentially stale widget snapshot.
      [...this.activeDropdownFilters.entries()]
        .filter(([componentId, filter]) => componentId !== currentComponentId && filter.field === dropdownField)
        .forEach(([componentId]) => this.activeDropdownFilters.delete(componentId));
      if (dropdownField && dropdownValues.length) {
        this.activeDropdownFilters.set(currentComponentId, { field: dropdownField, values: dropdownValues });
      } else {
        this.activeDropdownFilters.delete(currentComponentId);
      }
      // Build the combined filter list only after this dropdown's current
      // selection has been registered. Otherwise the request omits the
      // dropdown that was just changed (for example Application).
      payload['filters'] = this.activeDropdownFilterPayload();
      this.applyDropdownFiltersToClientWidgets();
      // Constrained to the author's picked targets — see
      // configuredDropdownTargetIds. Resolved BEFORE payload['targets'] is
      // overwritten below, since that is where the picks are stored.
      const rebuilt = this.widgets
        .filter((rec) => String(rec.widget?.componentId ?? '') !== String(widget?.componentId ?? '')
          // Explicit picker choice wins outright, whatever the target's type
          // (a chart OR another dropdown/input) — see the matching fix above.
          && (configured.size ? configured.has(String(rec.widget?.componentId ?? '')) : this.isChartWidget(rec.widget)))
        .map((rec) => ({ componentId: String(rec.widget?.componentId ?? ''), label: rec.widget?.title ?? '', field: payload['field'] ?? '' }))
        .filter((target) => !!target.componentId);
      // Only overwrite when we actually resolved chart targets. If the rebuild
      // comes back empty (e.g. chart componentIds not where expected), keep the
      // targets that were PERSISTED with the dropdown at publish — otherwise
      // we'd wipe them and the `!targetIds.length` guard below would skip the
      // compute call entirely (the "dropdown compute not firing" symptom).
      if (rebuilt.length) {
        payload['targets'] = rebuilt;
      } else if (!Array.isArray(payload['targets'])) {
        payload['targets'] = [];
      }
    }
    payload['otherInputs'] = this.otherActiveInputValues(String(widget?.componentId ?? ''));
    widget.payload = payload;
    if (String(widget?.type ?? '').toLowerCase() === 'dropdown') {
      this.persistDropdownValue(String(widget?.componentId ?? ''), value);
    }

    const targetIds = this.extractTargetIds(payload['targets']);
    const activeFilterValues = this.activeDropdownFilterValues();
    if (targetIds.length && Object.keys(activeFilterValues).length) {
      this.widgets = this.widgets.map((rec) => targetIds.includes(String(rec.widget?.componentId ?? ''))
        ? { ...rec, widget: { ...rec.widget, activeFilterValues } }
        : rec);
    }
    if (!targetIds.length && !this.hasCustomApiCall(widget)) {
      this.recomputeGeneration += 1;
      this.recomputingComponentIds = new Set();
      return;
    }

    const sideEffectInputs = this.sideEffectInputsFor(widget);
    this.recomputingComponentIds = new Set([...targetIds, ...sideEffectInputs.map((s) => String(s.widget?.componentId ?? ''))]);
    const generation = ++this.recomputeGeneration;
    // Snapshot this request's OWN generation for each of its targets — the
    // response later only gets dropped for a target some NEWER request has
    // since claimed, not for every target just because some unrelated widget
    // changed in between (see targetGeneration's declaration comment).
    const targetIdsAtRequestTime = targetIds.map((id) => {
      const next = (this.targetGeneration.get(id) ?? 0) + 1;
      this.targetGeneration.set(id, next);
      return { id, generation: next };
    });
    const stillCurrentTargetIds = () => targetIdsAtRequestTime
      .filter(({ id, generation: g }) => this.targetGeneration.get(id) === g)
      .map(({ id }) => id);

    const runMainCompute = () => this.subs.add(
      this.resolveInputComputeCall(widget?.apiConfig, this.computeUrlForWidget(widget), payload, {
        // EVERY payload key is a token, so a body template can pull `{dataset}`,
        // `{targets}`, `{bde_relation}`, `{dashboardId}`, `{isMultiSelect}`, …
        // instead of hardcoding them. The explicit three stay last so the LIVE
        // value/componentId/field always win over any stale payload copy.
        ...payload,
        value,
        componentId: widget?.componentId,
        field: payload['field'],
      }).pipe(
        finalize(() => { if (generation === this.recomputeGeneration) this.recomputingComponentIds = new Set(); }),
      ).subscribe({
        next: (response) => {
          const currentIds = stillCurrentTargetIds();
          if (currentIds.length) this.applyRecomputeResults(response, currentIds);
          // The slider/input value itself is a direct user edit — always
          // persist it (unlike the on-open dataset refresh, there's no need to
          // diff first; every call here is a real, intentional override).
          this.scheduleAutoPersistOverride();
        },
        error: (error) => console.warn('[dvm-published] input recompute failed:', error),
      }),
    );

    if (!sideEffectInputs.length) {
      runMainCompute();
      return;
    }

    // Replay Price Inflation %/Cost Inflation %'s own compute call FIRST -
    // with whatever value they currently show, even default/zero - so their
    // cross-referenced values are freshly cached before SSA $'s own call
    // resolves against them.
    const sideEffectRequests = sideEffectInputs.map(({ widget: sideWidget }) => {
      const sidePayload = { ...(sideWidget?.payload ?? {}), otherInputs: this.otherActiveInputValues(String(sideWidget?.componentId ?? '')) };
      return this.http.post<Record<string, unknown>>(this.computeUrlForWidget(sideWidget), sidePayload).pipe(
        map((response) => ({ sideWidget, response, error: null as unknown })),
        catchError((error) => of({ sideWidget, response: null, error })),
      );
    });

    this.subs.add(
      forkJoin(sideEffectRequests).subscribe((results) => {
        results.forEach(({ sideWidget, response, error }) => {
          if (response) {
            this.applyRecomputeResults(response, this.extractTargetIds(sideWidget?.payload?.targets));
          } else if (error) {
            console.warn('[dvm-published] SSA $ side-effect recompute for', sideWidget?.componentId, 'failed:', error);
          }
        });
        runMainCompute();
      }),
    );
  }

  // ── File upload widget (published) — POST the file, re-render targets ──────
  private uploadingWidgetIds = new Set<string>();
  private fileUploadMessages: Record<string, string> = {};
  isFileUploading(widget: any): boolean { return this.uploadingWidgetIds.has(String(widget?.componentId ?? widget?.widgetId ?? '')); }
  fileUploadMessage(widget: any): string { return this.fileUploadMessages[String(widget?.componentId ?? widget?.widgetId ?? '')] || ''; }

  /** A file was picked on a published file-upload widget → base64-encode it into
   *  a JSON payload and POST to the compute API (JSON-only — multipart gives 415)
   *  tagged with the widget's targets, then apply the per-target results (reuses
   *  the input-widget recompute machinery). */
  onFilePicked(widget: any, file: File): void {
    if (!file) return;
    const key = String(widget?.componentId ?? widget?.widgetId ?? '');
    const targetIds = this.extractTargetIds(widget?.payload?.targets ?? widget?.targets);

    this.uploadingWidgetIds = new Set(this.uploadingWidgetIds).add(key);
    this.fileUploadMessages = { ...this.fileUploadMessages, [key]: `Uploading ${file.name}…` };
    if (targetIds.length) this.recomputingComponentIds = new Set(targetIds);
    const generation = ++this.recomputeGeneration;

    this.fileToBase64(file).then((base64) => {
      const payload: Record<string, unknown> = {
        ...(widget?.payload ?? {}),
        componentId: String(widget?.componentId ?? ''),
        componentType: 'INPUT',
        inputType: 'FILE_UPLOAD',
        fileName: file.name,
        fileType: file.type,
        fileContent: base64,
        // The compute API requires every INPUT to carry a `value` (same field
        // dropdowns/sliders send); a file-upload's value IS the file.
        value: base64,
        defaultValue: base64,
        targets: targetIds.map((componentId) => ({ componentId })),
      };
      this.subs.add(
        this.resolveInputComputeCall(widget?.apiConfig, this.computeUrlForWidget(widget), payload, {
          value: base64,
          componentId: widget?.componentId,
          fileName: file.name,
          fileType: file.type,
        }).pipe(
          finalize(() => {
            this.uploadingWidgetIds.delete(key);
            this.uploadingWidgetIds = new Set(this.uploadingWidgetIds);
            if (generation === this.recomputeGeneration) this.recomputingComponentIds = new Set();
          }),
        ).subscribe({
          next: (response) => {
            if (generation === this.recomputeGeneration && targetIds.length) this.applyRecomputeResults(response, targetIds);
            this.fileUploadMessages = { ...this.fileUploadMessages, [key]: `Uploaded ${file.name}` };
          },
          error: () => {
            this.fileUploadMessages = { ...this.fileUploadMessages, [key]: `Upload failed for ${file.name}` };
          },
        }),
      );
    }).catch(() => {
      this.uploadingWidgetIds.delete(key);
      this.uploadingWidgetIds = new Set(this.uploadingWidgetIds);
      this.recomputingComponentIds = new Set();
      this.fileUploadMessages = { ...this.fileUploadMessages, [key]: `Could not read ${file.name}` };
    });
  }

  /** Read a File as a base64 string (no data-URL prefix — just the base64). */
  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = String(reader.result || '');
        const comma = result.indexOf(',');
        resolve(comma >= 0 ? result.slice(comma + 1) : result);
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }

  private extractTargetIds(rawTargets: unknown): string[] {
    if (!Array.isArray(rawTargets)) return [];
    return rawTargets
      .map((target) => {
        const rec = target as Record<string, unknown>;
        return String(rec?.['componentId'] ?? '');
      })
      .map((id) => id.trim())
      .filter(Boolean);
  }


  private applyRecomputeResults(response: Record<string, unknown> | null, targetIds: string[]): void {
    if (!targetIds.length) return;
    const results = (response?.['results'] ?? []) as Array<Record<string, unknown>>;
    if (!Array.isArray(results) || !results.length) return;
    const allowedIds = new Set(targetIds);
    results.forEach((entry) => {
      if (!recomputeEntryHasData(entry)) return;
      const result = (entry?.['result'] ?? entry) as Record<string, unknown>;
      const componentId = String(entry?.['componentId'] ?? result?.['componentId'] ?? '').trim();
      const rec = this.findRecomputeTargetWidget(componentId, resolveRecomputeChartType(entry), allowedIds);
      if (!rec) return;
      if (this.isInputWidget(rec.widget)) {
        this.applyValueToInputWidget(rec.id, rec.widget, entry);
      } else {
        this.applyComputeResultToWidget(rec.id, rec.widget, entry);
      }
      this.setWidgetStatus(rec.id, 'ready');
    });
  }

  private findRecomputeTargetWidget(componentId: string, chartType: string, allowedIds: Set<string>): PublishedWidgetRec | undefined {
    if (componentId && allowedIds.has(componentId)) {
      const direct = this.widgets.find((item) => String(item.widget?.componentId ?? '').trim() === componentId);
      if (direct) return direct;
    }

    if (!chartType) return undefined;
    // Type fallback for when the backend didn't echo a componentId — BUT only
    // when it's unambiguous. Previously this returned the FIRST allowed widget
    // of the matching type, so a dropdown/input driving two same-type charts
    // (or pointing at a stale id after a target was regenerated) silently drove
    // the WRONG chart. Only fall back when exactly ONE allowed target has this
    // type; otherwise refuse to guess.
    const sameType = this.widgets.filter((item) => {
      const id = String(item.widget?.componentId ?? '').trim();
      return allowedIds.has(id) && this.layoutTypeForWidget(item.widget) === chartType;
    });
    return sameType.length === 1 ? sameType[0] : undefined;
  }

  /** A target that is itself an input control just needs its live `value`
   *  pushed in — the backend already computed it from the driving control's
   *  formula. Unlike a chart, that has to land in TWO places: `payload.value`
   *  (so this control can, in turn, drive its OWN targets with the fresh
   *  value) and whatever field the actual rendered slider/input-box widget
   *  reads its displayed value from.
   *
   *  A DROPDOWN target is different: its INPUT result is a cascading refresh
   *  of its SELECTABLE OPTIONS (`result.data: [{value}, ...]`), not a scalar
   *  value to display — treating it like a slider/input-box wrote the raw
   *  object array into `payload.value` (rendering as "[object Object]") and
   *  never touched `optionsList`, the field the dropdown actually renders its
   *  choices from, so the UI never visibly updated. */
  /** Chooses between a widget's configured custom "API Call" (URL/method/body
   *  template, set in the builder's Interactions popup and carried on
   *  `widget.apiConfig` by buildTileWidget) and the built-in generate/
   *  recompute POST. When enabled, ONLY the custom call fires; its response is
   *  still expected in the same `{ results: [{ componentId, result }] }`
   *  shape applyRecomputeResults reads — mirrors dvm-layout-builder's
   *  resolveInputComputeCall so both surfaces behave identically. */
  /** True when this control carries an ENABLED custom API call with a URL —
   *  i.e. a change on it must fire a request regardless of whether any target
   *  charts resolved. Mirrors the guard inside `resolveInputComputeCall`. */
  private hasCustomApiCall(widget: any): boolean {
    const apiConfig = widget?.apiConfig;
    return !!apiConfig?.enabled && !!String(apiConfig?.url ?? '').trim();
  }

  private resolveInputComputeCall(
    apiConfig: {
      enabled?: boolean;
      url?: string;
      method?: string;
      bodyTemplate?: string;
      mode?: 'replace' | 'parallel';
      targets?: string[];
    } | undefined,
    fallbackUrl: string,
    fallbackPayload: Record<string, unknown>,
    tokens: Record<string, unknown>,
  ): Observable<Record<string, unknown>> {
    if (!apiConfig?.enabled || !apiConfig.url?.trim()) {
      return this.http.post<Record<string, unknown>>(fallbackUrl, fallbackPayload);
    }
    const custom$ = this.customApiCall$(apiConfig, tokens);
    if (apiConfig.mode !== 'parallel') return custom$;

    // PARALLEL mode: the custom endpoint owns `apiConfig.targets`; every other
    // target stays on the built-in compute call. Both requests go out at once
    // (forkJoin) and their `results` arrays are merged, so one target can be
    // served by a microservice while the rest come back from compute as usual.
    const customIds = new Set((apiConfig.targets ?? []).map(String).filter(Boolean));
    const computeTargets = this.targetsExcluding(fallbackPayload, customIds);
    // Nothing left for compute — the custom call already answers for everything.
    if (!computeTargets.length) return custom$;
    const compute$ = this.http.post<Record<string, unknown>>(fallbackUrl, { ...fallbackPayload, targets: computeTargets });
    return forkJoin([custom$, compute$]).pipe(
      map(([customResponse, computeResponse]) => this.mergeComputeResponses(customResponse, computeResponse, customIds)),
    );
  }

  /** The configured custom request on its own (no compute fallback). */
  private customApiCall$(
    apiConfig: { url?: string; method?: string; bodyTemplate?: string },
    tokens: Record<string, unknown>,
  ): Observable<Record<string, unknown>> {
    const method = (apiConfig.method || 'POST').toUpperCase();
    if (method === 'GET') {
      // A GET has no body, and the config UI hides the body editor for it — so
      // previously the control's value never left the browser and the endpoint
      // was called with nothing. Carry the same tokens the body template would
      // have received as query params instead (arrays repeat the key).
      return this.http.get<Record<string, unknown>>(apiConfig.url!, { params: this.buildApiCallParams(tokens) });
    }
    const body = this.buildApiCallBody(apiConfig.bodyTemplate, tokens);
    if (method === 'PUT') return this.http.put<Record<string, unknown>>(apiConfig.url!, body);
    if (method === 'PATCH') return this.http.patch<Record<string, unknown>>(apiConfig.url!, body);
    return this.http.post<Record<string, unknown>>(apiConfig.url!, body);
  }

  /** A compute payload's `targets` minus the ids the custom call already owns. */
  private targetsExcluding(payload: Record<string, unknown>, excluded: Set<string>): Array<Record<string, unknown>> {
    const targets = Array.isArray(payload?.['targets']) ? (payload['targets'] as Array<Record<string, unknown>>) : [];
    return targets.filter((target) => !excluded.has(String(target?.['componentId'] ?? '')));
  }

  /** Merge a parallel pair of `{ results: [...] }` responses. The custom call is
   *  authoritative for the ids it owns, so any compute entry for one of those is
   *  dropped rather than racing it. Every other key comes from the compute
   *  response (it is the canonical shape) with the custom one layered under it. */
  private mergeComputeResponses(
    customResponse: Record<string, unknown> | null,
    computeResponse: Record<string, unknown> | null,
    customIds: Set<string>,
  ): Record<string, unknown> {
    const entriesOf = (response: Record<string, unknown> | null): Array<Record<string, unknown>> =>
      Array.isArray(response?.['results']) ? (response!['results'] as Array<Record<string, unknown>>) : [];
    const customEntries = entriesOf(customResponse);
    const ownedByCustom = new Set(
      customEntries.map((entry) => String(entry?.['componentId'] ?? '')).filter(Boolean),
    );
    const computeEntries = entriesOf(computeResponse).filter((entry) => {
      const id = String(entry?.['componentId'] ?? '');
      return !ownedByCustom.has(id) && !customIds.has(id);
    });
    return {
      ...(customResponse ?? {}),
      ...(computeResponse ?? {}),
      results: [...customEntries, ...computeEntries],
    };
  }

  /** Fills `{token}` placeholders in a JSON body template.
   *
   *  - `"{value}"` (the token IS the whole JSON string) → the token's native
   *    JSON value, so an array stays an array and a number stays a number.
   *  - `["{value}"]` (bracket-wrapped, array-valued token) → that same array,
   *    NOT a nested one.
   *  - `{value}` embedded in a longer string → stringified (objects as JSON).
   *
   *  Unknown tokens are left as literal text; if that makes the result invalid
   *  JSON the tokens object is sent instead of an empty body, with a warning. */
  private buildApiCallBody(bodyTemplate: string | undefined, tokens: Record<string, unknown>): unknown {
    const template = String(bodyTemplate ?? '').trim() || '{}';
    const filled = template
      // `["{value}"]` — a one-element array whose only item is an ARRAY-valued
      // token — collapses to that array rather than nesting it ([["Sany"]]).
      // A multi-select dropdown's value is already a list, and wrapping the
      // token in brackets is the natural thing to write when the example
      // payload shows `"value": ["Sany"]`, so both spellings must work.
      .replace(/\[\s*"\{(\w+)\}"\s*\]/g, (match, key) =>
        (key in tokens && Array.isArray(tokens[key]) ? JSON.stringify(tokens[key]) : match))
      .replace(/"\{(\w+)\}"/g, (match, key) => (key in tokens ? JSON.stringify(tokens[key]) : match))
      .replace(/\{(\w+)\}/g, (match, key) => {
        if (!(key in tokens)) return match;
        const token = tokens[key];
        // Embedded in a longer string: objects/arrays stringify as JSON (not
        // "[object Object]"), scalars as themselves.
        return token !== null && typeof token === 'object' ? JSON.stringify(token) : String(token ?? '');
      });
    try {
      return JSON.parse(filled);
    } catch {
      // Returning `{}` here silently dropped the whole payload — the endpoint
      // got an empty object and no indication why. A template is invalid JSON
      // mainly when it carries a token this control doesn't supply (it stays as
      // a literal `{foo}`), so fall back to sending the tokens themselves
      // rather than nothing, and say so.
      console.warn('[dvm] custom API body template is not valid JSON after token substitution; sending tokens instead:', filled);
      return { ...tokens };
    }
  }

  /** Query params for a GET custom API call: every token that has a value, with
   *  arrays repeated as multiple entries of the same key. */
  private buildApiCallParams(tokens: Record<string, unknown>): HttpParams {
    let params = new HttpParams();
    Object.entries(tokens).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      if (Array.isArray(value)) {
        value.forEach((entry) => { params = params.append(key, String(entry)); });
      } else {
        params = params.set(key, String(value));
      }
    });
    return params;
  }

  private applyValueToInputWidget(id: string, widget: any, entry: Record<string, unknown>): void {
    if (String(widget?.type ?? '').toLowerCase() === 'dropdown') {
      const result = (entry?.['result'] ?? entry) as Record<string, unknown>;
      const rawOptions = Array.isArray(result?.['data']) ? (result['data'] as unknown[]) : [];
      const componentId = String(widget?.componentId ?? '');
      const freshOptions = rawOptions
        .map((item) => (item && typeof item === 'object' ? String((item as Record<string, unknown>)['value'] ?? '') : String(item ?? '')))
        .filter(Boolean);
      // Never let a dropdown LOSE an option it has already legitimately shown
      // for this exact combination of other active filters (e.g. Vertical) —
      // a narrower response later (whatever causes it — a race, a stale
      // request, backend flakiness) would otherwise silently drop it (e.g.
      // "MXDR" vanishing from Application under Vertical=Marine). Scoped by a
      // fingerprint of the OTHER active filters, so this never leaks options
      // across a genuinely different context (Mining's own narrower list
      // stays untouched by whatever Marine has cached).
      const optionsList = this.mergeAndCacheDropdownOptions(
        componentId,
        this.dropdownContextFingerprint(componentId),
        freshOptions,
      );
      // Drop any previously selected value no longer offered by the refreshed
      // option set, same as a native cascading filter.
      const currentValue: string[] = Array.isArray(widget?.payload?.value) ? widget.payload.value : [];
      const value = currentValue.filter((v) => optionsList.includes(String(v)));
      const field = String(widget?.payload?.field ?? '');
      if (componentId) {
        if (field && value.length) this.activeDropdownFilters.set(componentId, { field, values: value.map(String) });
        else this.activeDropdownFilters.delete(componentId);
      }
      // Refresh this dropdown's OWN stored otherInputs/filters here too — not
      // just `value`. This runs whenever a dropdown is refreshed as a TARGET
      // of another filter's change (e.g. Application/Region when Vertical
      // changes), and until now that never touched otherInputs, so it stayed
      // frozen at whatever it was the last time THIS dropdown was directly
      // changed by the user (or its published default) — one full selection
      // behind the actual current filter state. Recomputing it here, from the
      // live widget list at the moment this response lands, keeps it current
      // even when the user never touches this dropdown directly.
      const nextWidget = {
        ...widget,
        optionsList,
        payload: {
          ...(widget?.payload ?? {}),
          value,
          filters: this.activeDropdownFilterPayload(),
          otherInputs: this.otherActiveInputValues(componentId),
        },
        value,
      };
      this.widgets = this.widgets.map((rec) => (rec.id === id ? { ...rec, widget: nextWidget } : rec));
      return;
    }

    const fields = buildRecomputedWidgetFields(entry, { widgetId: String(widget?.widgetId ?? id) });
    const value = fields?.['value'];
    if (value === undefined) return;
    const isSlider = String(widget?.type ?? '').toLowerCase() === 'slider';
    const nextWidget = {
      ...widget,
      payload: { ...(widget?.payload ?? {}), value },
      ...(isSlider
        ? { data: { ...(widget?.data ?? {}), data: { ...(widget?.data?.data ?? {}), rangeStart: value, rangeEnd: value } } }
        : { value }),
    };
    this.widgets = this.widgets.map((rec) => (rec.id === id ? { ...rec, widget: nextWidget } : rec));
  }
  private applyComputeResultToWidget(id: string, widget: any, responseOrEntry: Record<string, unknown>): boolean {
    // Table-matrix targets re-render from raw rows (or a drill tree) — the
    // shared buildRecomputedWidgetFields has no table-matrix case, so handle it
    // here: swap the response rows into the widget's staticData (or drilldownData
    // for a drill-down table), preserving its capabilities.
    if (this.isTableMatrixWidget(widget)) {
      const entry = (responseOrEntry?.['result'] ?? responseOrEntry) as Record<string, unknown>;
      const rows = this.extractTableRows(entry);
      const caps = (widget?.capabilities ?? {}) as Record<string, unknown>;
      const isDrill = caps['tableType'] === 'drilldown' || (caps['typeFlags'] as Record<string, unknown> | undefined)?.['drilldown'] === true;
      // Keep `data.data` in step with staticData — see withFreshTableSnapshot:
      // it is the copy that gets persisted and re-promoted on the next load.
      const patch = isDrill
        ? { drilldownData: this.rebuildPublishedDrilldownData(id, widget, entry) }
        : { staticData: rows, data: { ...((widget?.data as Record<string, unknown>) ?? {}), data: rows } };
      this.widgets = this.widgets.map((rec) => rec.id === id
        ? { ...rec, widget: this.normalizePublishedWidget({ ...rec.widget, ...patch }) }
        : rec);
      return true;
    }
    const normalizedEntry = this.normalizeComputeEntry(widget, responseOrEntry);
    if (!recomputeEntryHasData(normalizedEntry)) return false;
    const fields = buildRecomputedWidgetFields(normalizedEntry, {
      widgetId: String(widget?.widgetId ?? id),
      title: widget?.title,
      yAxisName: widget?.yAxisName,
      xAxisName: widget?.xAxisName,
      kpiField: widget?.payload?.field,
      format: widget?.format,
      payload: widget?.payload,
      capabilities: widget?.capabilities,
    });
    if (!fields) return false;

    this.widgets = this.widgets.map((rec) => rec.id === id
      ? { ...rec, widget: this.normalizePublishedWidget({ ...rec.widget, ...fields, onWidgetValueChange: rec.widget?.onWidgetValueChange }) }
      : rec);
    return true;
  }

  /** A Drill-Down table-matrix target got a fresh compute tree: rebuild its
   *  lib-drill-down `data` through the SAME shared builder the DVM builder
   *  bakes with at publish time, then fold it into the existing widget data so
   *  the tile's baked extras (visual rules, export toggles, year filter…)
   *  survive. Editable levels/measures are re-derived from the widget's compute
   *  payload (each flagged dimension/measure carries `editable: true`), falling
   *  back to the capabilities mirror, and — for a tile predating both — to the
   *  editable markers already on the rendered headers. Swapping the RAW entry
   *  in (the old behaviour) gave lib-drill-down no staticData/headers, so the
   *  table neither re-rendered nor kept its editable cells. */
  private rebuildPublishedDrilldownData(id: string, widget: any, entry: Record<string, unknown>): Record<string, unknown> {
    const caps = (widget?.capabilities ?? {}) as Record<string, unknown>;
    const existing = (widget?.drilldownData ?? caps['drilldownData']) as Record<string, unknown> | undefined;
    const payload = (widget?.payload ?? {}) as Record<string, unknown>;
    const dimensions = (payload['dimensions'] ?? caps['dimensions']) as DrillDownEditConfig['dimensions'];
    const measures = (payload['measures'] ?? caps['measures']) as DrillDownEditConfig['measures'];
    // Merged Drill-Down + Pivot in CLIENT mode: the recompute answers with FLAT
    // rows (the cross-dataset flat contract), so rebuild the hierarchy / pivot
    // columns / formula columns locally from the widget's saved config before
    // handing the tree to the shared builder. Legacy backend-tree widgets pass
    // the entry straight through.
    const list = <T>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);
    const computedColumns = list<{ name: string; expression: string }>(payload['computedColumns'] ?? caps['computedColumns']);
    const formulaEvaluator = (expression: string, names: string[]) => this.formulaService.buildRowEvaluator(expression, names);
    const source = (payload['dataMode'] ?? caps['drillDataMode']) === 'client'
      ? drillTreeToResponse(buildDrillDownTreeFromRows(this.extractTableRows(entry), {
          dimensions: list<DrillClientConfig['dimensions'][number]>(dimensions),
          columnDimensions: list<NonNullable<DrillClientConfig['columnDimensions']>[number]>(payload['columnDimensions'] ?? caps['columnDimensions']),
          measures: list<DrillClientConfig['measures'][number]>(measures),
          // Totals picker: the bottom total row only when it includes "bottom".
          grandTotalRow: drillTotalsAtBottom(normalizeDrillTotalsPlacement(payload['totalsPlacement'] ?? caps['totalsPlacement'])),
          computedColumns,
          columnOrder: list<string>(payload['columnOrder'] ?? caps['columnOrder']),
          columnOrderPreset: list<string>(payload['columnOrderPreset'] ?? caps['columnOrderPreset']),
          columnValueOrderPreset: list<string>(payload['columnValueOrderPreset'] ?? caps['columnValueOrderPreset']),
          rowTotalColumns: list<string>(payload['rowTotalColumns'] ?? caps['rowTotalColumns']),
          rawLeafRows: caps['rawLeafRows'] === true,
          ...(caps['headerGroupBy'] === 'column' ? { headerGroupBy: 'column' as const } : {}),
          ...(caps['rowTotal'] === true
            ? {
                rowTotal: true,
                ...(caps['rowTotalLabel'] ? { rowTotalLabel: String(caps['rowTotalLabel']) } : {}),
                ...(caps['rowTotalPosition'] === 'left' ? { rowTotalPosition: 'left' as const } : {}),
              }
            : {}),
        }, formulaEvaluator))
      : entry;
    const fresh = buildDrillDownWidgetData(source, {
      widgetId: String(existing?.['widgetId'] ?? `${widget?.widgetId ?? id}-drill`),
      fallbackHeaderName: (existing?.['title'] as string | undefined) ?? widget?.title,
      // Table-matrix tiles keep their Format-popup keys (decimals, …) on capabilities.
      format: (widget?.format as Record<string, unknown> | undefined) ?? caps,
      dimensions,
      measures,
      computedColumns,
      formulaEvaluator,
    });
    return mergeDrillDownWidgetData(existing, fresh, { inheritEditable: !Array.isArray(dimensions) && !Array.isArray(measures) });
  }

  private normalizeComputeEntry(widget: any, responseOrEntry: Record<string, unknown>): Record<string, unknown> {
    if (responseOrEntry['result']) return responseOrEntry;
    const chartType = this.layoutTypeForWidget(widget);
    return {
      componentId: widget?.componentId,
      result: {
        ...responseOrEntry,
        chartType: responseOrEntry['chartType'] ?? chartType,
        componentId: responseOrEntry['componentId'] ?? widget?.componentId,
      },
    };
  }

  /** The builder posts a flat column list for client-mode drill-down tables.
   * Their persisted payload describes how to render the tree, so posting it
   * verbatim makes the compute endpoint look for fields in the wrong dataset. */
  private computePayloadForWidget(widget: any): Record<string, unknown> {
    const payload = (widget?.payload ?? {}) as Record<string, unknown>;
    if (!this.isTableMatrixWidget(widget) || payload['dataMode'] !== 'client' || payload['subType'] !== 'drilldown') return payload;
    const dataset = String(payload['dataset'] ?? widget?.capabilities?.dataset ?? '');
    const columns: Array<{ dataset: string; field: string; displayName: string }> = [];
    const seen = new Set<string>();
    const add = (items: unknown): void => {
      if (!Array.isArray(items)) return;
      items.forEach((item) => {
        const field = String(item?.field ?? '').trim();
        const source = String(item?.dataset || dataset).trim();
        const key = `${source}|${field}`;
        if (!field || !source || seen.has(key)) return;
        seen.add(key);
        columns.push({ dataset: source, field, displayName: field });
      });
    };
    add(payload['dimensions']);
    add(payload['columnDimensions']);
    add(payload['measures']);
    // Year-scoped measures need their year column in the flat response too.
    // The builder saves that column as a hidden helper MEASURE on the dataset
    // that OWNS it (drillYearHelperMeasures), so it is normally in `columns`
    // already. Only tiles published before the helper existed need this
    // fallback — and it must never re-request a column that is already there
    // against the measure's OWN dataset, where it may not exist: the compute
    // API rejects the unknown column and the whole tile fell back to its
    // snapshot ("Fresh compute failed").
    if (Array.isArray(payload['measures'])) {
      const requested = (field: string): boolean => Array.from(seen).some((key) => key.endsWith(`|${field}`));
      add((payload['measures'] as Array<Record<string, unknown>>)
        .filter((measure) => measure['yearScope'] === 'latest' && measure['yearField'] && !requested(String(measure['yearField'])))
        .map((measure) => ({ field: measure['yearField'], dataset: measure['dataset'] })));
    }
    return {
      componentId: payload['componentId'] ?? widget?.componentId,
      componentType: 'TABLE',
      tableType: 'NORMAL',
      subType: 'drilldown',
      dashboardId: payload['dashboardId'],
      title: payload['title'] ?? widget?.title,
      dataset,
      columns,
      aggregation: 'none',
      ...(payload['format'] ? { format: payload['format'] } : {}),
    };
  }

  private computeUrlForWidget(widget: any): string {
    // Published Table & Matrix widgets historically persisted `computeUrl`,
    // while chart widgets use `recomputeUrl`. Support both so a drill-down can
    // refresh from its live source instead of rendering only its baked snapshot.
    return resolveSelfServiceApiUrl(widget?.recomputeUrl || widget?.computeUrl, '/components/compute');
  }

  /** WHY a compute call failed, for the tile banner + console: the HTTP status
   *  and the backend's own message (or "network error" for status 0 — CORS,
   *  offline, DNS). Every failure used to read the same, leaving the Network
   *  tab as the only clue. */
  private describeComputeFailure(error: unknown): string {
    const err = (error ?? {}) as { status?: number; statusText?: string; message?: string; error?: unknown };
    const body = err.error;
    const bodyRecord = body && typeof body === 'object' ? (body as Record<string, unknown>) : null;
    const bodyMessage = typeof body === 'string'
      ? body
      : String(bodyRecord?.['message'] ?? bodyRecord?.['error'] ?? bodyRecord?.['detail'] ?? '');
    const status = Number(err.status ?? 0);
    const head = status > 0 ? `HTTP ${status}` : 'network error';
    const detail = String(bodyMessage || (status > 0 ? err.statusText : err.message) || '').replace(/\s+/g, ' ').trim();
    return detail ? `${head}: ${detail.slice(0, 160)}` : head;
  }

  private setWidgetStatus(id: string, status: PublishedWidgetRec['status'], error?: string): void {
    this.widgets = this.widgets.map((rec) => rec.id === id ? { ...rec, status, error } : rec);
  }

  /** The componentIds a dropdown was CONFIGURED to target (build form's "Target
   *  chart(s)" picker, persisted on its payload). Empty set = none picked, which
   *  keeps the historical "all charts" global-filter default. */
  private configuredDropdownTargetIds(payload: Record<string, unknown>): Set<string> {
    const raw = payload?.['targets'];
    if (!Array.isArray(raw)) return new Set();
    return new Set(
      raw
        .map((entry) => (entry && typeof entry === 'object'
          ? String((entry as Record<string, unknown>)['componentId'] ?? '')
          : String(entry ?? '')))
        .filter(Boolean),
    );
  }

  private isInputWidget(widget: any): boolean {
    const type = String(widget?.type ?? '').toLowerCase();
    return type === 'slider' || type === 'input-box' || type === 'dropdown';
  }

  /** Edit mode forces CLASSIC dropdown tiles compact (span 2) — but the
   *  button/tile display modes render every option as a visible pill and need
   *  the tile's real persisted height. */
  /** A chart/table widget's notes (builder-form "Notes" accordion → `tileNotes`)
   *  when there is something to draw — rendered UNDER the card, outside it.
   *  LEGACY: the first cut kept a table's notes in `capabilities.notes`. */
  tileNotesOf(widget: any): { heading?: string; items?: string[]; listStyle?: string; position?: string } | null {
    const notes = widget?.tileNotes ?? widget?.capabilities?.notes;
    if (!notes || notes.enabled !== true) return null;
    const hasItems = Array.isArray(notes.items) && notes.items.length > 0;
    return String(notes.heading ?? '').trim() || hasItems ? notes : null;
  }

  isCompactDropdownTile(widget: any): boolean {
    if (String(widget?.type ?? '').toLowerCase() !== 'dropdown') return false;
    const mode = String(widget?.displayMode ?? 'dropdown');
    return mode !== 'buttons' && mode !== 'tiles';
  }

  /** Resolves a tile's grid-row value. Edit mode shows the persisted footprint
   *  (with the classic-dropdown compact override); view mode additionally
   *  applies the measured dropdown corrections — see the
   *  viewSpanFixes/viewShiftFixes doc above. */
  /** Resolves a tile's grid-column. Edit mode = the saved 12-col footprint.
   *  View mode = the same footprint on the fine grid, except KPI tiles that
   *  share a row: those split the row's total width EQUALLY (the reference
   *  design's four identical stat cards), with any indivisible remainder
   *  spread one fine-track at a time — invisible at 1/240 of the width. */
  tileGridColumn(entry: { rec: PublishedWidgetRec; item: PublishedLayoutItem }): string {
    const item = entry.item;
    if (this.editMode) return `${item.x + 1} / span ${item.colSpan}`;
    const K = this.VIEW_GRID_COL_MULTIPLIER;
    const fix = this.resolveKpiRowFixes()[item.id];
    if (fix) return `${fix.start + 1} / span ${fix.span}`;
    return `${item.x * K + 1} / span ${item.colSpan * K}`;
  }

  private resolveKpiRowFixes(): Record<string, { start: number; span: number }> {
    const key = this.layout.map((i) => `${i.id}:${i.x}:${i.y}:${i.colSpan}`).join('|');
    if (key === this.kpiRowFixesKey) return this.kpiRowFixes;
    this.kpiRowFixesKey = key;
    const K = this.VIEW_GRID_COL_MULTIPLIER;
    const rows = new Map<number, PublishedLayoutItem[]>();
    for (const item of this.layout) {
      const type = String(this.widgetRecById(item.id)?.widget?.type ?? '').toLowerCase();
      if (type !== 'kpi-card') continue;
      const list = rows.get(item.y) ?? [];
      list.push(item);
      rows.set(item.y, list);
    }
    const fixes: Record<string, { start: number; span: number }> = {};
    for (const items of rows.values()) {
      if (items.length < 2) continue;
      items.sort((a, b) => a.x - b.x);
      const start = items[0].x * K;
      const end = Math.max(...items.map((i) => (i.x + i.colSpan) * K));
      const total = end - start;
      const base = Math.floor(total / items.length);
      let remainder = total - base * items.length;
      let cursor = start;
      for (const item of items) {
        const span = base + (remainder > 0 ? 1 : 0);
        if (remainder > 0) remainder -= 1;
        fixes[item.id] = { start: cursor, span };
        cursor += span;
      }
    }
    this.kpiRowFixes = fixes;
    return fixes;
  }

  /** Standalone classic dropdown tiles stacked in ONE column (same x / colSpan,
   *  nothing else wedged between them) read as a filter sidebar — but each is
   *  its own top-level tile, so unlike a group card there is no element to
   *  carry the reference UI's single tinted panel. View mode therefore draws a
   *  purely presentational backdrop grid item behind each such run (the same
   *  column, from the first control's row to the last one's measured end) and
   *  tags the members (rail-first / rail-last) so their padding insets the
   *  controls from the panel edge. Layout, persistence and edit mode are
   *  untouched: the panel is never a widget. Buttons / Tiles display modes are
   *  left bare, as before. */
  private resolveRailGroups(): PublishedLayoutItem[][] {
    const key = this.layout.map((i) => `${i.id}:${i.x}:${i.y}:${i.colSpan}:${i.rowSpan}`).join('|')
      + '#' + this.widgets.map((w) => `${w.id}:${String(w.widget?.type ?? '')}:${String(w.widget?.displayMode ?? '')}`).join('|');
    if (key === this.railGroupsKey) return this.railGroups;
    this.railGroupsKey = key;
    const sharesColumns = (a: PublishedLayoutItem, b: PublishedLayoutItem): boolean =>
      a.x < b.x + b.colSpan && a.x + a.colSpan > b.x;
    const byColumn = new Map<string, PublishedLayoutItem[]>();
    for (const item of this.layout) {
      if (!this.isCompactDropdownTile(this.widgetRecById(item.id)?.widget)) continue;
      const colKey = `${item.x}:${item.colSpan}`;
      const list = byColumn.get(colKey) ?? [];
      list.push(item);
      byColumn.set(colKey, list);
    }
    const groups: PublishedLayoutItem[][] = [];
    for (const items of byColumn.values()) {
      items.sort((a, b) => a.y - b.y);
      let run: PublishedLayoutItem[] = [];
      for (const item of items) {
        const prev = run[run.length - 1];
        const interrupted = !!prev && this.layout.some(
          (other) => other.id !== prev.id && other.id !== item.id && other.y > prev.y && other.y < item.y && sharesColumns(other, item),
        );
        if (prev && !interrupted) {
          run.push(item);
          continue;
        }
        if (run.length >= 2) groups.push(run);
        run = [item];
      }
      if (run.length >= 2) groups.push(run);
    }
    const roles: Record<string, 'first' | 'middle' | 'last'> = {};
    for (const group of groups) {
      group.forEach((item, idx) => {
        roles[item.id] = idx === 0 ? 'first' : idx === group.length - 1 ? 'last' : 'middle';
      });
    }
    this.railGroups = groups;
    this.railRoleById = roles;
    return groups;
  }

  /** Rail membership of a tile ('first' | 'middle' | 'last'), or null when
   *  the tile is not part of a stacked dropdown run. View mode only. */
  railRole(itemId: string): 'first' | 'middle' | 'last' | null {
    if (this.editMode) return null;
    this.resolveRailGroups();
    return this.railRoleById[itemId] ?? null;
  }

  /** Grid placement of every filter-rail backdrop for the current view
   *  geometry (includes the measured dropdown span/shift corrections). */
  get filterRailBackdrops(): Array<{ key: string; gridColumn: string; gridRow: string }> {
    if (this.editMode) return [];
    const K = this.VIEW_GRID_COL_MULTIPLIER;
    const RM = this.VIEW_GRID_ROW_MULTIPLIER;
    return this.resolveRailGroups().map((group) => {
      const first = group[0];
      let start = Number.POSITIVE_INFINITY;
      let end = 0;
      for (const item of group) {
        const rowStart = item.y * RM + 1 + (this.viewShiftFixes[item.id] ?? 0);
        const rowEnd = rowStart + item.rowSpan * RM + (this.viewSpanFixes[item.id] ?? 0);
        start = Math.min(start, rowStart);
        end = Math.max(end, rowEnd);
      }
      return {
        key: group.map((i) => i.id).join('|'),
        gridColumn: `${first.x * K + 1} / span ${first.colSpan * K}`,
        gridRow: `${start} / ${end}`,
      };
    });
  }

  tileGridRow(entry: { rec: PublishedWidgetRec; item: PublishedLayoutItem }): string {
    const item = entry.item;
    if (this.editMode) {
      const span = this.isCompactDropdownTile(entry.rec.widget) && !item.customSize ? 2 : item.rowSpan;
      return `${item.y + 1} / span ${span}`;
    }
    const extra = this.viewSpanFixes[item.id] ?? 0;
    const shift = this.viewShiftFixes[item.id] ?? 0;
    return `${(item.y * this.VIEW_GRID_ROW_MULTIPLIER) + 1 + shift} / span ${(item.rowSpan * this.VIEW_GRID_ROW_MULTIPLIER) + extra}`;
  }

  /** Zone-triggering no-op: a viewport resize re-wraps the pill rows, so the
   *  dropdown-tile measurement in ngAfterViewChecked must run again. The
   *  listener itself needs no body — its presence makes Angular run change
   *  detection on resize, which is what re-triggers the measuring pass. */
  @HostListener('window:resize')
  onViewportResizeRemeasure(): void {}

  ngAfterViewChecked(): void {
    this.syncDropdownTileViewFixes();
  }

  /** Measures every rendered dropdown card (height:fit-content in view mode,
   *  so offsetHeight is the natural control height) and derives view-only
   *  span/shift corrections. Controls can expand past the saved band or shrink
   *  from an over-tall saved footprint; both cases are applied top-to-bottom so
   *  charts below keep their relative geometry while the empty space collapses.
   *  Applied via setTimeout so the grid restyles on the NEXT change-detection
   *  pass — this runs inside ngAfterViewChecked, where a synchronous mutation
   *  would throw ExpressionChanged. Converges: the card's natural height
   *  doesn't depend on the correction, so the second pass measures identical
   *  values and stops. */
  private syncDropdownTileViewFixes(): void {
    if (this.editMode || this.loading || this.viewFixApplyScheduled) return;

    const host = this.hostEl.nativeElement as HTMLElement;
    const canvas = host.querySelector<HTMLElement>('.dvm-published-canvas');
    const canvasStyles = canvas ? getComputedStyle(canvas) : null;
    const ROW_PX = this.VIEW_GRID_ROW_PX;
    const GAP_PX = canvasStyles ? (parseFloat(canvasStyles.rowGap) || 4) : 4;
    const tiles = host.querySelectorAll<HTMLElement>('.dvm-published-tile.dropdown-tile[data-tile-id]');

    // n rows (with their internal gaps) cover n*ROW + (n-1)*GAP pixels.
    const neededRows = new Map<string, number>();
    tiles.forEach((tileEl) => {
      const id = tileEl.getAttribute('data-tile-id') ?? '';
      if (!id || tileEl.offsetHeight <= 0) return;
      neededRows.set(id, Math.max(1, Math.ceil((tileEl.offsetHeight + GAP_PX) / (ROW_PX + GAP_PX))));
    });

    const nextSpans: Record<string, number> = {};
    const nextShifts: Record<string, number> = {};
    const displayY = (item: PublishedLayoutItem): number => item.y * this.VIEW_GRID_ROW_MULTIPLIER;
    const shiftedY = (item: PublishedLayoutItem): number => displayY(item) + (nextShifts[item.id] ?? 0);
    const sharesColumns = (a: PublishedLayoutItem, b: PublishedLayoutItem): boolean =>
      a.x < b.x + b.colSpan && a.x + a.colSpan > b.x;

    const dropdowns = this.layout
      .filter((item) => neededRows.has(item.id))
      .sort((a, b) => a.y - b.y);

    for (const dropdown of dropdowns) {
      const savedSpan = dropdown.rowSpan * this.VIEW_GRID_ROW_MULTIPLIER;
      const effectiveSpan = this.clamp(neededRows.get(dropdown.id) ?? 1, 1, 36);
      const spanDelta = effectiveSpan - savedSpan;
      if (spanDelta !== 0) nextSpans[dropdown.id] = spanDelta;
      const top = shiftedY(dropdown);
      const bottom = top + effectiveSpan;
      const columnBelow = this.layout.filter(
        (item) => item.id !== dropdown.id && shiftedY(item) > top && sharesColumns(item, dropdown),
      );
      if (!columnBelow.length) continue;
      const nearestTop = Math.min(...columnBelow.map((item) => shiftedY(item)));
      const delta = bottom - nearestTop;
      if (delta === 0) continue;
      // Shift ONLY the tiles in this dropdown's own column(s). `delta` is derived
      // from `columnBelow` — the gap between THIS control's measured bottom and
      // the next tile beneath it in the same column — so it is meaningless for a
      // tile in a different column. Applying it to every tile below (the old
      // `below`) made a sidebar of stacked filters accumulate one delta per
      // dropdown onto the chart/table columns, dragging them up into each other:
      // the reported "tiles sit on top of each other" in view mode. Edit mode was
      // unaffected because it renders the raw persisted footprint with no fixes.
      for (const item of columnBelow) nextShifts[item.id] = (nextShifts[item.id] ?? 0) + delta;
    }

    const changed =
      JSON.stringify(nextSpans) !== JSON.stringify(this.viewSpanFixes) ||
      JSON.stringify(nextShifts) !== JSON.stringify(this.viewShiftFixes);
    if (!changed) return;

    this.viewFixApplyScheduled = true;
    setTimeout(() => {
      this.viewFixApplyScheduled = false;
      this.viewSpanFixes = nextSpans;
      this.viewShiftFixes = nextShifts;
    }, 0);
  }

  private isChartWidget(widget: any): boolean {
    return !!widget?.type && !this.isInputWidget(widget);
  }

  private isClientSideWidget(widget: any): boolean {
    const type = String(widget?.type);
    const isKnownClientType = type === 'bar-column' || type === 'line-area' || type === 'scatter-bubble';
    return isKnownClientType && !widget?.payload;
  }

  // ── Cross-filter (published view) — mirrors the builder's generic engine ───

  /** True when a widget renders client-side and can publish/react to cross-filters. */
  private isCrossFilterWidget(widget: any): boolean {
    return DvmPublishedDashboardComponent.CROSS_FILTER_TYPES.has(String(widget?.type ?? '')) && Array.isArray(widget?.staticData);
  }

  /** Waterfall widgets react to cross-filters via SERVER recompute (aggregated
   *  bars, no client rows) — needs the persisted compute payload to re-run. */
  private isRecomputeCrossFilterWidget(widget: any): boolean {
    return String(widget?.type ?? '') === 'waterfall-chart' && Array.isArray(widget?.waterfallBars) && !!widget?.payload;
  }

  /** The column a widget publishes as its cross-filter key: its persisted
   *  `filterKey` override, else its category column from capabilities. */
  private publishedFilterKey(widget: any): string {
    const key = String(widget?.filterKey ?? '').trim();
    if (key) return key;
    return String(widget?.capabilities?.category ?? '');
  }

  /** The widget ids that REACT to a source widget: its persisted `listenerTileIds`
   *  when set, else every OTHER cross-filter-capable widget (client-side charts
   *  plus recompute listeners like the waterfall) as the default. */
  private publishedListenerIds(sourceRec: PublishedWidgetRec): string[] {
    const explicit = sourceRec.widget?.listenerTileIds;
    if (Array.isArray(explicit)) return explicit.map(String);
    return this.widgets
      .filter((r) => r.id !== sourceRec.id && (this.isCrossFilterWidget(r.widget) || this.isRecomputeCrossFilterWidget(r.widget)))
      .map((r) => r.id);
  }

  /** Handle a cross-filter click emitted by a published chart. Sets/clears the
   *  active filter (multi-select) and re-renders the reacting widgets. */
  onPublishedCrossFilter(sourceRec: PublishedWidgetRec, event: { column: string; value: string | null; values?: string[] }): void {
    const column = this.publishedFilterKey(sourceRec.widget) || event.column;
    const values = Array.isArray(event.values) ? event.values : (event.value ? [event.value] : []);
    this.activeCrossFilter = values.length ? { column, values, sourceId: sourceRec.id } : null;
    // Notify the backend which value was clicked and where the filter applied.
    // Filtering itself stays client-side (instant), so this call is best-effort:
    // errors never block the UI. Skipped when the filter is cleared (no values).
    this.postCrossFilterEvent(sourceRec, column, values);
    this.applyPublishedCrossFilter();
  }

  /** Best-effort POST to /{entity}/dynamic_filter describing the cross-filter
   *  click: the source chart, the column it publishes, and the clicked value(s),
   *  plus the widgets that react. Fire-and-forget — the UI never waits on it and
   *  a failure is swallowed (client-side filtering already happened). Config-free:
   *  the dataset comes from the source widget's own payload. */
  private postCrossFilterEvent(sourceRec: PublishedWidgetRec, column: string, values: string[]): void {
    if (!values.length) return; // filter cleared — nothing to report
    const w: any = sourceRec.widget || {};
    const multiDataset = Array.isArray(w?.payload?.datasets) ? String(w.payload.datasets[0] ?? '') : '';
    const dataset = String(w?.payload?.dataset ?? '').trim() || multiDataset.trim() || String(w?.dataset ?? '').trim();
    if (!dataset) return; // no dataset to scope the call to
    const url = buildSelfServiceApiUrl(`/${encodeURIComponent(dataset)}/dynamic_filter`);
    const body = {
      uuid: null,
      // The cross-filter selection: which column, which clicked value(s), which
      // source chart published it, and which tiles react. `filters` is the shape
      // a filter-aware backend would consume (column IN values).
      crossFilter: {
        column,
        values,
        sourceWidgetId: sourceRec.id,
        listenerWidgetIds: this.publishedListenerIds(sourceRec),
      },
      filters: [{ column, operator: 'IN', values }],
    };
    this.subs.add(
      this.http.post(url, body).pipe(catchError(() => of(null))).subscribe(),
    );
  }

  /** Re-render every reacting client-side widget from its ORIGINAL rows filtered
   *  by column IN values (or restore all rows when cleared). Generic across every
   *  registered cross-filter chart type; the source widget keeps its own data.
   *  RECOMPUTE listeners (waterfall) can't filter client-side — they re-run their
   *  compute payload with the filter attached (see recomputePublishedWaterfall). */
  private applyPublishedCrossFilter(): void {
    const filter = this.activeCrossFilter;
    const sourceRec = filter ? this.widgets.find((r) => r.id === filter.sourceId) : undefined;
    const listeners = sourceRec ? new Set(this.publishedListenerIds(sourceRec)) : new Set<string>();
    const filterValues = filter ? new Set(filter.values) : null;

    this.widgets = this.widgets.map((rec) => {
      if (!this.isCrossFilterWidget(rec.widget)) return rec;
      if (filter && rec.id === filter.sourceId) return rec;
      const original = this.crossFilterOriginalRows.get(rec.id) ?? [];
      const reacts = !!filter && listeners.has(rec.id) && original.some((row) => filter.column in row);
      const rows = reacts && filterValues
        ? original.filter((row) => filterValues.has(String(row[filter!.column] ?? '')))
        : original;
      // New widget object ref (and staticData ref) so the chart's ngOnChanges fires.
      return { ...rec, widget: { ...rec.widget, staticData: rows } };
    });

    // Server-recompute listeners (waterfall bridges): aggregated charts can only
    // react by recomputing on the filtered data. Fire per-widget recomputes for
    // listening waterfalls; restore any other/cleared waterfall to its cached
    // unfiltered bars (instant, no API call).
    this.widgets.forEach((rec) => {
      if (!this.isRecomputeCrossFilterWidget(rec.widget)) return;
      if (filter && rec.id === filter.sourceId) return;
      if (filter && listeners.has(rec.id)) this.recomputePublishedWaterfall(rec, filter);
      else this.restorePublishedWaterfall(rec);
    });
  }

  /** Original (unfiltered) bars per waterfall widget, stashed once when the first
   *  filtered recompute lands so clearing the filter restores instantly. */
  private waterfallOriginalBars = new Map<string, unknown[]>();
  /** Monotonic per-widget sequence so a slow recompute response can never
   *  clobber a newer filter state (or a restore) that raced past it. */
  private waterfallFilterSeq = new Map<string, number>();

  /** Re-run a waterfall widget's persisted compute payload with the active
   *  cross-filter attached and redraw the bridge from the response. Best-effort:
   *  if the backend ignores/errs on `filters`, the current bars stay untouched. */
  private recomputePublishedWaterfall(rec: PublishedWidgetRec, filter: { column: string; values: string[] }): void {
    const widget: any = rec.widget || {};
    const seq = (this.waterfallFilterSeq.get(rec.id) ?? 0) + 1;
    this.waterfallFilterSeq.set(rec.id, seq);
    const payload = {
      ...(widget.payload as Record<string, unknown>),
      filters: [{ column: filter.column, operator: 'IN', values: filter.values }],
    };
    this.subs.add(
      this.http.post<Record<string, unknown>>(this.computeUrlForWidget(widget), payload).subscribe({
        next: (response) => {
          if (this.waterfallFilterSeq.get(rec.id) !== seq) return; // stale response
          // The compute API may answer a bare bars array or {data: bars} — wrap it
          // into the entry shape the shared recompute builder consumes.
          const entry = this.normalizeComputeEntry(widget, Array.isArray(response) ? { data: response } : response);
          const fields = buildRecomputedWidgetFields(entry, {
            widgetId: rec.id,
            title: widget.title,
            format: widget.format as Record<string, unknown> | undefined,
            payload: widget.payload as Record<string, unknown> | undefined,
          });
          const bars = fields?.['waterfallBars'];
          if (!Array.isArray(bars) || !bars.length) return; // nothing usable — keep current view
          const current = this.widgets.find((r) => r.id === rec.id);
          if (!current) return;
          // Stash the originals ONCE (first filtered redraw) for instant restore.
          if (!this.waterfallOriginalBars.has(rec.id)) {
            this.waterfallOriginalBars.set(rec.id, (current.widget as any)?.waterfallBars ?? []);
          }
          this.widgets = this.widgets.map((r) =>
            r.id === rec.id ? { ...r, widget: { ...r.widget, ...fields } } : r,
          );
        },
        // Best-effort: a failed recompute leaves the widget's current bars alone.
        error: () => undefined,
      }),
    );
  }

  /** Put a waterfall widget back on its unfiltered bars (kept from before the
   *  first filtered recompute). Also invalidates any in-flight recompute. */
  private restorePublishedWaterfall(rec: PublishedWidgetRec): void {
    this.waterfallFilterSeq.set(rec.id, (this.waterfallFilterSeq.get(rec.id) ?? 0) + 1);
    const original = this.waterfallOriginalBars.get(rec.id);
    if (!original) return; // never showed filtered bars
    this.waterfallOriginalBars.delete(rec.id);
    this.widgets = this.widgets.map((r) =>
      r.id === rec.id
        ? {
            ...r,
            widget: {
              ...r.widget,
              waterfallBars: original,
              option: buildWaterfallOption(original as any[], (r.widget as any)?.format),
            },
          }
        : r,
    );
  }

  // ── Dropdown → client-side chart filtering (published) ─────────────────────

  /** Active dropdown selections, keyed by the dropdown widget's componentId, so
   *  SEVERAL dropdowns compose: each re-apply starts from every client-side
   *  widget's ORIGINAL rows and applies all active filters in turn. */
  private activeDropdownFilters = new Map<string, { field: string; values: string[] }>();

  private seedActiveDropdownFiltersFromWidgets(): void {
    this.activeDropdownFilters.clear();
    this.widgets.forEach((rec) => {
      const widget = rec.widget;
      if (String(widget?.type ?? '').toLowerCase() !== 'dropdown') return;
      const componentId = String(widget?.componentId ?? '');
      const field = String(widget?.payload?.field ?? '');
      const value = widget?.payload?.value;
      const values = Array.isArray(value) ? value.map(String) : value == null || value === '' ? [] : [String(value)];
      if (componentId && field && values.length) {
        this.activeDropdownFilters.set(componentId, { field, values });
      }
    });
  }

  /** Compose all active dropdown selections for backend recompute requests. */
  private activeDropdownFilterPayload(): Array<{ column: string; operator: 'IN'; values: string[] }> {
    return [...this.activeDropdownFilters.values()]
      .filter((filter) => filter.field && filter.values.length)
      .map((filter) => ({ column: filter.field, operator: 'IN' as const, values: [...filter.values] }));
  }

  private activeDropdownFilterValues(): Record<string, unknown> {
    return [...this.activeDropdownFilters.values()].reduce((values, filter) => {
      if (filter.field && filter.values.length) {
        values[filter.field] = filter.values.length === 1 ? filter.values[0] : [...filter.values];
      }
      return values;
    }, {} as Record<string, unknown>);
  }

  /** Filter the client-side chart family (bar-column / line-area / scatter-
   *  bubble) locally from the captured original rows — the compute API has no
   *  usable recompute for these types. A widget ignores filters whose field its
   *  rows don't carry; with no active filters it's restored to its full rows. */
  private applyDropdownFiltersToClientWidgets(): void {
    const filters = [...this.activeDropdownFilters.values()].filter((f) => f.field && f.values.length);
    this.widgets = this.widgets.map((rec) => {
      if (!this.isCrossFilterWidget(rec.widget)) return rec;
      // Seed the original snapshot if load-time capture missed this widget.
      if (!this.crossFilterOriginalRows.has(rec.id)) {
        this.crossFilterOriginalRows.set(rec.id, [...(rec.widget.staticData as Array<Record<string, unknown>>)]);
      }
      const original = this.crossFilterOriginalRows.get(rec.id) ?? [];
      let rows = original;
      filters.forEach(({ field, values }) => {
        if (!original.some((row) => field in row)) return;
        const wanted = new Set(values.map(String));
        rows = rows.filter((row) => wanted.has(String(row[field] ?? '')));
      });
      // New widget/staticData refs so the chart's ngOnChanges fires.
      return { ...rec, widget: { ...rec.widget, staticData: rows } };
    });
  }

  /** Snapshot each cross-filter widget's original staticData rows (once, on load)
   *  so a filter can always be reversed. Called after widgets are set. */
  private captureCrossFilterOriginals(): void {
    this.crossFilterOriginalRows.clear();
    this.widgets.forEach((rec) => {
      if (this.isCrossFilterWidget(rec.widget)) {
        this.crossFilterOriginalRows.set(rec.id, [...(rec.widget.staticData as Array<Record<string, unknown>>)]);
      }
    });
  }

  /** Table & Matrix tiles render from their baked-in snapshot (staticData /
   *  drilldownData) — they must NOT be POSTed to the chart compute API on open
   *  (a plain table payload 400s there). Excluded from compute-on-open and from
   *  being a navigation-filter recompute target. */
  private isTableMatrixWidget(widget: any): boolean {
    return String(widget?.type ?? '').toLowerCase() === 'table-matrix';
  }

  /** Widgets that render purely from their published snapshot and must never be
   *  marked 'loading' for the on-open recompute. Drill-down table-matrix tiles
   *  are the exception: they carry a client/server compute payload and must be
   *  rebuilt from the live response so new rule rows/attributes are visible. */
  private isSnapshotOnlyWidget(widget: any): boolean {
    // 'popup' is display-only (its table rows are snapshotted at build time):
    // no recompute path ever resolves it, so — like table-matrix/file-upload —
    // it must NEVER enter 'loading' at either init site or its spinner would
    // stick forever (the group-card lesson).
    const type = String(widget?.type ?? '').toLowerCase();
    const tableType = String(widget?.capabilities?.tableType ?? widget?.tableType ?? '').toLowerCase();
    const isDrilldown = tableType === 'drilldown'
      || widget?.capabilities?.typeFlags?.drilldown === true
      || widget?.typeFlags?.drilldown === true;
    return (this.isTableMatrixWidget(widget) && !isDrilldown) || type === 'file-upload' || type === 'popup';
  }

  /** Input controls (Slider / Input Box / Dropdown / File Upload) take NO
   *  card-header formatting (by request). Gating the CSS-var bindings on this
   *  (rather than only not offering the controls in the builder) means a stale
   *  `format` published while the feature was briefly enabled can never paint
   *  those tiles. */
  /** Re-apply a published group card's own design (Format popup in the builder:
   *  border colour/width, card background, corner radius, transparent) as inline
   *  styles. Mirrors DvmLayoutBuilderComponent.groupCardStyleMap so the card
   *  looks the same either side of publish. Empty for every other widget type. */
  groupCardStyles(widget: any): Record<string, string> {
    if (String(widget?.type ?? '') !== 'group-card') return {};
    const format = widget?.format as Record<string, unknown> | undefined;
    const styles: Record<string, string> = {};
    if (!format) return styles;
    const transparent = format['cardTransparent'] === true || format['cardTransparent'] === 'true';
    const border = String(format['cardBorderColor'] ?? '').trim();
    const width = Number(format['cardBorderWidth']);
    const background = String(format['cardBackground'] ?? '').trim();
    const radius = Number(format['cardCornerRadius']);

    if (transparent) {
      styles['background'] = 'transparent';
      styles['border-color'] = 'transparent';
      styles['box-shadow'] = 'none';
    } else {
      if (background) styles['background'] = background;
      if (border) styles['border-color'] = border;
    }
    if (Number.isFinite(width)) styles['border-width'] = `${Math.max(0, width)}px`;
    if (Number.isFinite(radius)) styles['border-radius'] = `${Math.max(0, radius)}px`;
    return styles;
  }

  /** Tile-wide font overrides (the shared "Font family" / "Font size" Format
   *  controls) exposed as --dvm-tile-font-* CSS vars on the published tile:
   *  HTML widgets (KPI, inputs, card headers, drill-down) inherit them; canvas
   *  charts receive the same values through their capabilities instead.
   *  Mirrors DvmLayoutBuilderComponent.tileFontFamily/tileFontSizePx. */
  tileFontFamily(format?: Record<string, unknown>): string | null {
    const family = String(format?.['fontFamily'] ?? '').trim();
    return family || null;
  }
  tileFontSizePx(format?: Record<string, unknown>): string | null {
    const size = Number(format?.['fontSize']);
    return Number.isFinite(size) && size > 0 ? `${size}px` : null;
  }

  takesHeaderFormat(widget: any): boolean {
    // Controls render bare (no card header bar), so there is nothing for the
    // card-header colors to paint. Popup is one of them: its own dialog header
    // is colored from the build form's headerBg/headerText instead, applied
    // inline by the popup component (the dialog is reparented to <body>, so
    // tile-level CSS vars would never reach it anyway).
    const type = String(widget?.type ?? '').toLowerCase();
    return type !== 'slider' && type !== 'input-box' && type !== 'dropdown' && type !== 'file-upload' && type !== 'popup';
  }

  /** Pull flat rows out of a compute/upload result for a table target (common
   *  envelope shapes: data / Data / rows / nested data.data / top-level array). */
  private extractTableRows(entry: Record<string, unknown>): Array<Record<string, unknown>> {
    if (Array.isArray(entry)) return entry as Array<Record<string, unknown>>;
    const candidate = entry['data'] ?? entry['Data'] ?? entry['rows'];
    if (Array.isArray(candidate)) return candidate as Array<Record<string, unknown>>;
    const nested = (candidate as { data?: unknown } | undefined)?.data;
    return Array.isArray(nested) ? (nested as Array<Record<string, unknown>>) : [];
  }

  private isKpiWidget(widget: any): boolean {
    return String(widget?.type ?? '').toLowerCase() === 'kpi-card';
  }

  private isSliderWidget(widget: any): boolean {
    return String(widget?.type ?? '').toLowerCase() === 'slider';
  }
  private isVisualChartWidget(widget: any): boolean {
    return this.isChartWidget(widget) && !this.isKpiWidget(widget);
  }
  private layoutTypeForWidget(widget: any): string {
    const type = String(widget?.type ?? '').toLowerCase();
    if (type === 'waterfall-chart') return 'WATERFALL';
    if (type === 'drill-down') return 'DRILL_DOWN_TABLE';
    if (type === 'horizontal-bar-chart') return 'HORIZONTAL_BAR_CHART';
    if (type === 'kpi-card') return 'KPI';
    if (type === 'bar-column') return 'BAR';
    if (type === 'line-area') return 'LINE_AREA';
    if (type === 'scatter-bubble') return 'SCATTER_BUBBLE';
    if (type === 'dropdown') return 'DROPDOWN';
    if (type === 'table-matrix') return 'TABLE_MATRIX';
    if (type === 'file-upload') return 'FILE_UPLOAD';
    if (type === 'popup') return 'POPUP';
    return String(widget?.layoutType ?? widget?.payload?.chartType ?? '').toUpperCase();
  }

  private minColSpanForItem(id: string): number {
    const rec = this.widgets.find((item) => item.id === id);
    return this.minColSpanForWidget(rec?.widget);
  }

  private minRowSpanForItem(id: string): number {
    const rec = this.widgets.find((item) => item.id === id);
    return this.minRowSpanForWidget(rec?.widget);
  }

  private minColSpanForWidget(widget: any): number {
    if (this.isKpiWidget(widget)) return 1;
    if (this.isSliderWidget(widget)) return 2;
    // A popup trigger is a small control (an icon can sit in 1 column).
    if (String(widget?.type ?? '').toLowerCase() === 'popup') return 1;
    // A group card is a CONTAINER (usually of narrow input controls), not a
    // chart — without this branch it inherits the visual-chart minimum
    // (3 cols / 4 rows) and its width silently stops reducing at that floor,
    // both in the edge-drag resize clamp and the on-load colSpan re-clamp.
    if (this.isGroupCardWidget(widget)) return 2;
    return this.isVisualChartWidget(widget) ? 3 : 2;
  }

  private minRowSpanForWidget(widget: any): number {
    if (String(widget?.type ?? '').toLowerCase() === 'dropdown') return 1;
    if (String(widget?.type ?? '').toLowerCase() === 'popup') return 1;
    if (this.isKpiWidget(widget)) return 1;
    if (this.isSliderWidget(widget)) return 2;
    if (this.isGroupCardWidget(widget)) return 2;
    return this.isVisualChartWidget(widget) ? 4 : 2;
  }

  private isGroupCardWidget(widget: any): boolean {
    return String(widget?.type ?? '').toLowerCase() === 'group-card';
  }

  private buildSavedDashboardLayout(layoutJson: Record<string, unknown>) {
    return {
      columns: Number((layoutJson['layout'] as Record<string, unknown> | undefined)?.['columns'] ?? 12),
      items: this.layout.map((item, index) => ({ ...item, order: index })),
      pageContent: layoutJson['pageContent'],
    };
  }
  private async resolvePublishApi(layoutJson: Record<string, unknown>): Promise<string> {
    const fromLayout = String((layoutJson['apiEndpoints'] as Record<string, unknown> | undefined)?.['publishApi'] ?? '').trim();
    if (fromLayout) return resolveSelfServiceApiUrl(fromLayout);
    try {
      const config = await firstValueFrom(this.http.get<Record<string, unknown>>('/assets/self-service-config/dvm-layout-builder.json'));
      return resolveSelfServiceApiUrl(String((config?.['apiEndpoints'] as Record<string, unknown> | undefined)?.['publishApi'] ?? '').trim());
    } catch {
      return '';
    }
  }

  /** `uuid` present → this dashboard already has a `dashboard_layouts` row owned
   *  by this user, so re-saving must `update` that row instead of `insert`ing a
   *  duplicate one (publish/save were always `insert`, which is why re-saving
   *  the same dashboard kept appending new rows — see
   *  SelfServiceDashboardService.findOwnedDashboardLayoutRecord). */
  private buildPublishEnvelope(layoutJson: Record<string, unknown>, emailId: string, dashboardId: string, uuid: string | null = null): Record<string, unknown> {
    return {
      requestData: {
        email_id: emailId,
        role: 'User',
        layout_json: layoutJson,
        dashboard_id: dashboardId,
        ...(uuid ? { uuid } : {}),
      },
      // `operation` sits at the TOP (envelope) level, not inside requestData.
      operation: uuid ? 'update' : 'insert',
      responseList: [],
      entity: 'dashboard_layouts',
      batchId: 'test',
      workflowId: 'ABC-123',
      endpoints: [],
      version: '1.0',
    };
  }
  private buildPersistedLayoutJson(): Record<string, unknown> | null {
    if (!this.originalLayoutJson) return null;
    const next = this.deepCloneRecord(this.originalLayoutJson);
    const layoutById = new Map(this.layout.map((item, index) => [item.id, { ...item, index }]));
    const widgetById = new Map(this.widgets.map((rec) => [rec.id, rec.widget]));
    const count = new Map<string, number>();

    const visit = (node: any): void => {
      if (!node || typeof node !== 'object') return;
      if (node.type) {
        const id = this.resolveNodePublishedId(node, count);
        const liveWidget = widgetById.get(id);
        if (liveWidget) {
          const snapshot = this.serializableWidgetSnapshot(liveWidget);
          Object.keys(node).forEach((key) => delete node[key]);
          Object.assign(node, snapshot);
        }
        const layout = layoutById.get(id);
        if (layout) {
          node.layout = { ...(node.layout || {}), colSpan: layout.colSpan, rowSpan: layout.rowSpan, x: layout.x, y: layout.y, order: layout.index, customSize: !!layout.customSize };
          node.class = `col-${layout.colSpan}`;
          node.height = layout.rowSpan * this.EDIT_GRID_ROW_PX;
        }
        return;
      }
      if (Array.isArray(node.columns)) this.patchAndSortNodeList(node.columns, visit, layoutById, count);
      if (Array.isArray(node.rows)) this.patchAndSortNodeList(node.rows, visit, layoutById, count);
    };

    if (Array.isArray(next['pageContent'])) {
      this.patchAndSortNodeList(next['pageContent'] as any[], visit, layoutById, count);
    }

    // Per-node `layout.colSpan/rowSpan` (set above) is what dashboard.component.ts's
    // own buildDefaultLayout() needs to reconstruct x/y/w/h for every breakpoint on
    // load — but also mirror the SAME {id,colSpan,rowSpan,order} list here, keyed by
    // breakpoint (single "xl" breakpoint for now, this view has no responsive
    // breakpoints of its own), so `layout_json.layout` carries a breakpoint-keyed
    // snapshot alongside the plain column count, matching the shape other DVM
    // dashboard layouts persist.
    const existingLayout = (next['layout'] as Record<string, unknown> | undefined) ?? {};
    const layoutItems = this.layout.map((item, index) => {
      const rec = this.widgets.find((widget) => widget.id === item.id);
      return {
        id: item.id,
        order: index,
        colSpan: item.colSpan,
        rowSpan: item.rowSpan,
        x: item.x,
        y: item.y,
        componentId: String(rec?.widget?.componentId ?? ''),
        customSize: !!item.customSize,
      };
    });
    next['layout'] = {
      ...existingLayout,
      columns: Number(existingLayout['columns'] ?? 12),
      items: layoutItems,
      breakpoints: {
        ...((existingLayout['breakpoints'] as Record<string, unknown> | undefined) ?? {}),
        xl: layoutItems.map(({ id, colSpan, rowSpan, x, y, order, customSize }) => ({
          id,
          colSpan,
          rowSpan,
          x,
          y,
          order,
          customSize,
        })),
      },
    };

    const now = new Date().toISOString();
    next['updatedAt'] = now;
    next['publishedAt'] = now;
    next['status'] = 'published';
    return next;
  }

  private patchAndSortNodeList(
    list: any[],
    visit: (node: any) => void,
    _layoutById: Map<string, PublishedLayoutItem & { index: number }>,
    _count: Map<string, number>,
  ): void {
    list.forEach((node) => visit(node));
    if (!list.every((node) => node && typeof node === 'object' && node.type)) return;
    list.sort((a, b) => {
      const aOrder = Number(a?.layout?.order ?? Number.MAX_SAFE_INTEGER);
      const bOrder = Number(b?.layout?.order ?? Number.MAX_SAFE_INTEGER);
      return aOrder - bOrder;
    });
  }

  private serializableWidgetSnapshot(widget: any): Record<string, unknown> {
    const { onWidgetValueChange: _onWidgetValueChange, ...snapshot } = (widget || {}) as Record<string, unknown>;
    void _onWidgetValueChange;
    return toSerializable(snapshot);
  }

  private resolveNodePublishedId(node: any, count: Map<string, number>): string {
    const base = String(node?.widgetId ?? node?.id ?? `widget-${this.nextNodeOrdinal(count)}`).replace(/[^a-zA-Z0-9-_]/g, '-');
    const idx = (count.get(base) ?? 0) + 1;
    count.set(base, idx);
    return idx === 1 ? base : `${base}__${idx}`;
  }

  private nextNodeOrdinal(count: Map<string, number>): number {
    return Array.from(count.values()).reduce((sum, value) => sum + value, 0) + 1;
  }

  private deepCloneRecord(value: Record<string, unknown>): Record<string, unknown> {
    // NOT structuredClone: it throws on any function value, and stored chart
    // configs can carry one (e.g. an echarts tooltip/label `formatter`
    // callback) — toSerializable strips those (and breaks cycles) instead of
    // throwing, matching what JSON.stringify does silently at publish time.
    return toSerializable(value);
  }

  private createDashboardId(): string {
    return `dvm-dashboard-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  }
  private notifyChartsAboutResize(): void {
    window.requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
  }
  private classToColSpan(value: unknown): number | null {
    const match = String(value ?? '').match(/col-(\d+)/);
    return match ? Number(match[1]) : null;
  }


  private buildLayoutFromJson(
    layoutJson: Record<string, unknown> | null,
    widgets: PublishedWidgetRec[],
  ): PublishedLayoutItem[] {
    const saved = this.extractSavedLayoutItems(layoutJson, widgets);
    return saved.length ? saved : this.buildLayoutFromWidgets(widgets);
  }

  private extractSavedLayoutItems(
    layoutJson: Record<string, unknown> | null,
    widgets: PublishedWidgetRec[],
  ): PublishedLayoutItem[] {
    const layout = layoutJson?.['layout'] as Record<string, unknown> | undefined;
    const breakpoints = layout?.['breakpoints'] as Record<string, unknown> | undefined;
    const layoutItems = Array.isArray(layout?.['items'])
      ? layout['items'] as Array<Record<string, unknown>>
      : [];
    const rawItems = Array.isArray(breakpoints?.['xl'])
      ? breakpoints['xl'] as Array<Record<string, unknown>>
      : layoutItems;
    if (!rawItems.length) return [];

    const componentIdByLayoutId = new Map(
      layoutItems
        .map((item) => [String(item?.['id'] ?? '').trim(), String(item?.['componentId'] ?? '').trim()] as const)
        .filter(([id, componentId]) => !!id && !!componentId)
    );
    const widgetById = new Map(widgets.map((rec) => [rec.id, rec]));
    const widgetByComponentId = new Map(
      widgets
        .map((rec) => [String(rec.widget?.componentId ?? '').trim(), rec] as const)
        .filter(([componentId]) => !!componentId)
    );
    const seen = new Set<string>();
    const restored: Array<PublishedLayoutItem & { order: number }> = [];

    // Free-form placement is explicit x/y per item — older saved dashboards
    // (published before this) only ever carried colSpan/rowSpan/order, no
    // position, so detect which shape we're restoring rather than assuming.
    const hasExplicitPositions = rawItems.some((record) => Number.isFinite(Number(record?.['x'])) && Number.isFinite(Number(record?.['y'])));

    rawItems.forEach((record, fallbackOrder) => {
      const recordId = String(record?.['id'] ?? '').trim();
      const componentId = String(record?.['componentId'] ?? componentIdByLayoutId.get(recordId) ?? '').trim();
      const rec = widgetById.get(recordId) ?? widgetByComponentId.get(componentId);
      if (!rec || seen.has(rec.id)) return;

      seen.add(rec.id);
      const customSize = record['customSize'] === true || rec.widget?.layout?.customSize === true;
      restored.push({
        id: rec.id,
        colSpan: this.clamp(Number(record['colSpan'] ?? rec.widget?.layout?.colSpan ?? this.classToColSpan(rec.widget?.class) ?? 6), this.minColSpanForWidget(rec.widget), 12),
        rowSpan: this.clamp(Number(record['rowSpan'] ?? rec.widget?.layout?.rowSpan ?? Math.ceil(Number(rec.widget?.height ?? 384) / this.EDIT_GRID_ROW_PX)), this.minRowSpanForWidget(rec.widget), 18),
        x: hasExplicitPositions ? Math.max(0, Number(record['x'] ?? 0)) : 0,
        y: hasExplicitPositions ? Math.max(0, Number(record['y'] ?? 0)) : 0,
        customSize,
        order: Number(record['order'] ?? fallbackOrder),
      });
    });

    restored.sort((a, b) => a.order - b.order);

    // Legacy layout (order only, no x/y) — derive positions with the same
    // row-fill flow a brand-new dashboard gets, so it still renders sensibly
    // instead of every tile collapsing onto (0,0).
    const positioned = hasExplicitPositions ? restored : this.assignFlowPositions(restored, 0);

    // Any widget on the dashboard that ISN'T in the saved layout (added since
    // the last publish) needs a position too — start it below everything
    // already placed so it can't land on top of an existing tile regardless
    // of that tile's own x/y. EXCEPT group-card children: extractWidgets()
    // flattens them into `widgets` too (so compute/target-matching can find a
    // grouped chart like any other), but they were NEVER in the saved
    // layout.items to begin with (only the group-card itself is) — without
    // this exclusion they'd all fall into "not in the saved layout" and get
    // their own standalone grid position, rendering every grouped chart a
    // SECOND time as its own separate top-level tile.
    const startY = positioned.length ? Math.max(...positioned.map((item) => item.y + item.rowSpan)) : 0;
    const groupChildIds = this.groupChildWidgetIds(widgets);
    const newWidgets = widgets.filter((rec) => !seen.has(rec.id) && !groupChildIds.has(rec.id));
    const appended = this.assignFlowPositions(
      newWidgets.map((rec) => this.widgetToLayoutSeed(rec)),
      startY,
    );

    return [...positioned, ...appended].map(({ order: _order, ...item }) => item);
  }

  /** Row-fill flow layout (left→right, wrapping at 12 columns, using the
   *  tallest tile in a row to know where the next row starts) — the same
   *  simple algorithm a brand-new dashboard uses to arrange tiles before the
   *  user does any free-form dragging, and the fallback for legacy saved
   *  layouts that only ever recorded an order, not a position. */
  private assignFlowPositions<T extends { colSpan: number; rowSpan: number }>(
    items: T[],
    startY: number,
  ): Array<T & { x: number; y: number }> {
    let cursorX = 0;
    let cursorY = startY;
    let rowHeight = 0;
    return items.map((item) => {
      if (cursorX + item.colSpan > 12) {
        cursorX = 0;
        cursorY += rowHeight;
        rowHeight = 0;
      }
      const placed = { ...item, x: cursorX, y: cursorY };
      cursorX += item.colSpan;
      rowHeight = Math.max(rowHeight, item.rowSpan);
      return placed;
    });
  }

  /** Span-only fields for a widget, before a position is assigned — shared by
   *  `buildLayoutFromWidgets` (no saved layout at all) and the "newly added
   *  widget not in the saved layout" path above. */
  private widgetToLayoutSeed(rec: PublishedWidgetRec): { id: string; colSpan: number; rowSpan: number; customSize: boolean; order: number } {
    const customSize = rec.widget?.layout?.customSize === true;
    const isDropdown = String(rec.widget?.type ?? '').toLowerCase() === 'dropdown';
    // Older dropdowns were published with the generic input/card height of
    // four rows. Compact them automatically until the user explicitly
    // resizes the control in Edit Layout; that custom size is then persisted.
    const rowSpan = isDropdown && !customSize
      ? 1
      : Number(rec.widget?.layout?.rowSpan ?? Math.ceil(Number(rec.widget?.height ?? 384) / this.EDIT_GRID_ROW_PX));
    return {
      id: rec.id,
      colSpan: this.clamp(Number(rec.widget?.layout?.colSpan ?? this.classToColSpan(rec.widget?.class) ?? 6), this.minColSpanForWidget(rec.widget), 12),
      rowSpan: this.clamp(rowSpan, this.minRowSpanForWidget(rec.widget), 18),
      customSize,
      order: 0,
    };
  }

  /** The layout's single source of truth when there's no saved layout at all
   *  (brand-new dashboard) — each widget's own `layout.colSpan/rowSpan`
   *  (server-persisted by saveLayout()'s publish, or the dashboard builder's
   *  Generate/Save), flowed into positions left-to-right/top-to-bottom. */
  private buildLayoutFromWidgets(widgets: PublishedWidgetRec[]): PublishedLayoutItem[] {
    // Same exclusion as extractSavedLayoutItems: group-card children are only
    // ever rendered NESTED inside their card, never as their own top-level tile.
    const groupChildIds = this.groupChildWidgetIds(widgets);
    const topLevel = widgets.filter((rec) => !groupChildIds.has(rec.id));
    return this.assignFlowPositions(topLevel.map((rec) => this.widgetToLayoutSeed(rec)), 0)
      .map(({ order: _order, ...item }) => item);
  }

  /** Every widget id that lives INSIDE a group-card (its `children`), across
   *  every group-card on the dashboard — used to keep them out of the
   *  top-level layout-positioning paths above (they render nested, via
   *  `childWidgetIds` + widgetRecById() in the template, not as their own tile). */
  private groupChildWidgetIds(widgets: PublishedWidgetRec[]): Set<string> {
    const ids = new Set<string>();
    widgets.forEach((rec) => {
      if (rec.widget?.type === 'group-card' && Array.isArray(rec.widget?.childWidgetIds)) {
        (rec.widget.childWidgetIds as string[]).forEach((id) => ids.add(id));
      }
    });
    return ids;
  }

  private async resolveCurrentEmail(): Promise<string> {
    if (this.resolvedEmailMemo) return this.resolvedEmailMemo;
    let email = '';
    try {
      if (await this.keycloak.isLoggedIn()) {
        const profile = await this.keycloak.loadUserProfile();
        email = profile.email || '';
      }
    } catch { /* fall through */ }
    if (email) this.resolvedEmailMemo = email;
    return email;
  }

  private confirmOwnership(email: string): void {
    this.isOwner = this.dashboardService.isOwnedDashboardSlug(this.slug);
    this.syncToolbarState();
    if (this.isOwner || !email) return;
    this.subs.add(
      this.dashboardService.isDashboardOwnedByCurrentUser(this.slug, email).subscribe((owned) => {
        this.isOwner = owned;
        this.syncToolbarState();
      }),
    );
  }

  private clamp(value: number, min: number, max: number): number {
    const next = Number.isFinite(value) ? Math.round(value) : min;
    return Math.min(max, Math.max(min, next));
  }
}








